import {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Wrench } from 'lucide-react';
import { z } from 'zod';
import { conversationAgentStateSchema } from '@arcanum-hq/shared-types';
import clsx from 'clsx';
import { useAuth } from '@/hooks/use-auth';
import {
  useConversations,
  useConversation,
  useCreateConversation,
  useForkConversation,
  useUpgradeModelVersion,
} from '@/hooks/use-conversations-v2';
import { useStreaming } from '@/hooks/use-streaming-v2';
import { useConversationChannel } from '@/hooks/use-conversation-channel';
import { useConversationViews } from '@/hooks/use-conversation-views';
import { useCollectionState } from '@/hooks/use-collection-state';
import type { DiffOpV2, CollectionState } from '@arcanum-hq/shared-types';
import { useModel, useModels } from '@/hooks/use-models-v2';
import { useModelView } from '@/hooks/use-model-view';
import { useModelEdit } from '@/hooks/use-model-edit';
import { useBillingReadOnly } from '@/hooks/use-billing-readonly';
import {
  useConversationVersions,
  useCreateVersion,
} from '@/hooks/use-conversation-versions';
import { useActiveVersionDrift } from '@/hooks/use-active-version-drift';
import { queryKeys } from '@/lib/query-keys';
import { api, ApiError, MissingModelError } from '@/lib/api';
import { debugRealtime } from '@/lib/debug-realtime';
import {
  ConversationSidebar,
  ChatThread,
  ChatInput,
  StopButton,
  BuilderToolbar,
  VariablesPanel,
  NewConversationModal,
  ForkBanner,
  ModelUpgradeBanner,
  ShareForkBanner,
  ShareBackModal,
  MissingModelPanel,
  ForkSuccessBanner,
  ModelHeaderStrip,
  ManageVersionsButton,
  DealHeaderStrip,
  ActiveVersionPill,
  AdminViewingBanner,
} from '@/components/builder';
import {
  ModelLayout,
  LayoutUnavailablePanel,
  type ModelViewBridge,
} from '@/components/builder/model-view';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { EmptyState, Skeleton, Button } from '@/components/ui';
import { ModelOverviewPanel } from '@/components/builder/ModelOverviewPanel';
import { NarrativeBar } from '@/components/layout/NarrativeBar';
import {
  SignalOperatingAsset,
  SignalNewDevelopment,
  SignalAnchorListener,
} from '@/components/builder/signal';
import { SendDealLinkButton } from '@/components/builder/SendDealLinkButton';
import { SendDealLinkModal } from '@/components/builder/SendDealLinkModal';
import { BrowseVersionsModal } from '@/components/builder/BrowseVersionsModal';
import { RestoreVersionConfirm } from '@/components/builder/RestoreVersionConfirm';
import { InteractionSurface } from '@/components/builder/interaction';
import {
  EDIT_TABLE_EVENT,
  type EditTableEventDetail,
} from '@/components/builder/TableEditReceipt';
import type {
  InteractionRequest,
  MarketDefaultApplyInteraction,
  AliasMatchState,
  ApplyMarketDefaultsResult,
} from '@arcanum-hq/shared-types';
import { LocationInputModal } from '@/components/builder/LocationInputModal';
import {
  computeVersionReadiness,
  type ProvenanceEntry,
} from '@/lib/version-completeness';
import { ReadOnlyShareBuilder } from '@/components/builder/ReadOnlyShareBuilder';
import type { ChatMessage } from '@/components/builder/ChatThread';

/**
 * market-default-apply-ux Phase 2 ADR 0007 — derive from the barrel-exported
 * result type, NOT a separate barrel export from shared-types or tool-results.
 * `informationalSet` and `genuinelyUnmatchedSet` both carry this shape.
 */
type MarketFieldValueUnit =
  ApplyMarketDefaultsResult['informationalSet'][number];

/**
 * next-variable-curation Phase 5 Session 5.1 — mirrors the server-side
 * `MUTATING_TOOLS` set in `conversations.service.v2.ts:4492-4499` exactly.
 * The `latestToolPayloads` memo's `collectionState` extraction guards on this
 * set so the banner updates after ANY of the 5 mutating actions, not just
 * `set_inputs`. Drop one name here → banner goes stale after that action.
 */
const MUTATING_TOOLS = new Set([
  'set_inputs',
  'set_table_inputs',
  'apply_market_defaults',
  'accept_defaults',
  'set_property_address',
]);

/**
 * `?tab=workbench|signal|model` URL search-param schema. `.catch('workbench')`
 * absorbs arbitrary garbage in the URL — INCLUDING the legacy `?tab=conversation`
 * value from before agent-ux-optimization Phase 6 Session 3 (2026-05-08) — so
 * a stale link doesn't throw at the route's `validateSearch` boundary. No
 * back-compat redirect: legacy URLs degrade silently to `'workbench'` (the
 * direct successor to the old `'conversation'` tab). Model + Signal tabs are
 * hidden behind disabled states (NOT this schema) when respective
 * preconditions aren't met (`!modelId` for Model, `signal_variant === null`
 * for Signal) — the schema accepts the value either way.
 *
 * `?conversation=<uuid>` selects a specific conversation on initial route
 * load (link-sharing + bookmark restore). Invalid / non-uuid values resolve
 * to `undefined` and the route falls back to the auto-select-first-conversation
 * behavior. (model-view-ux-polish Phase 2 Session 2.1, 2026-04-27 — required
 * for the 404 friendly recovery branch to fire on URL navigation; previously
 * the URL param was silently ignored.)
 */
export const BUILDER_TABS = ['workbench', 'signal', 'model'] as const;
export type BuilderTab = (typeof BUILDER_TABS)[number];
export const builderSearchSchema = z.object({
  tab: z.enum(BUILDER_TABS).catch('workbench').default('workbench'),
  conversation: z.string().uuid().optional().catch(undefined),
  // external-sharing Phase 4 Session 4.1 — unblocks the Received tab's
  // [Open] button typed Link. Session 4.2 wires the read-only-share render
  // path that consumes this param.
  share: z.string().uuid().optional().catch(undefined),
  // admin-deal-oversight (ADR 0007): when adminView=1, the real
  // AuthModeBuilderPage renders in read-only mode (all mutation affordances
  // gated off). coerce.number so the URL string "1" parses as 1.
  adminView: z.coerce.number().optional().catch(undefined),
  // Phase 3 Session 5 deep-link params (additive; all default to undefined).
  // Anchor-resolution failure for any of these is silent — logged to
  // console + window event `arcanum:deep-link-resolution`, never an
  // error modal. Bad strings degrade to undefined via .catch(undefined).
  search: z.string().optional().catch(undefined),
  cell: z.string().optional().catch(undefined),
  namedRange: z.string().optional().catch(undefined),
  messageId: z.string().uuid().optional().catch(undefined),
  input: z.string().optional().catch(undefined),
  // Phase 6 Session 8 — Signal anchor params (silent-fail per the
  // pre-existing deep-link convention). `section` enumerates the 5
  // canonical signal sections; unknown values degrade to undefined.
  // `assumption` carries a namedRange string; the listener flashes the
  // matching `<AssumptionCard>` and silently no-ops if no card is
  // currently rendered (e.g. the assumption is on a different
  // pagination page).
  section: z
    .enum(['hero', 'property', 'story', 'comparison', 'assumptions'])
    .optional()
    .catch(undefined),
  assumption: z.string().optional().catch(undefined),
});

/**
 * In-route helper rendering the Model tab's content area.
 *
 * Owns the 4 mandatory states (loading, error, empty, success) for the
 * `/model-view` query and dispatches between `<ModelLayout>` (full payload)
 * and `<LayoutUnavailablePanel>` (layout-missing fallback). The
 * `onCreateNew` callback threads through to the panel's CTA so it can flip
 * the parent route's `setShowNewModal` state — the panel itself can't reach
 * the modal's state.
 *
 * Co-located here (not a separate file) to mirror the existing in-route
 * helper convention; Phase 4 swaps the placeholders inside `<ModelLayout>`
 * without touching this component.
 */
function ModelLayoutContainer({
  conversationId,
  onCreateNew,
  onViewInConversation,
  bridgeRef,
  onFullscreenChange,
  uiPrefs,
  cellParam,
  namedRangeParam,
  searchParam,
  onConsumeDeepLink,
  onSearchChange,
  readOnly = false,
}: {
  conversationId: string | null;
  onCreateNew: () => void;
  /** Phase 4 Session 3: callback for the View-in-Conversation toast link inside `<CellDetailPanel>`. */
  onViewInConversation?: () => void;
  /** Billing read-only gate — disables cell edits when usage >= 90%. */
  readOnly?: boolean;
  /** Phase 4 Session 4: route-level bridge ref for Realtime DiffOp fan-out. Forwarded to `<ModelLayout>`. */
  bridgeRef?: React.MutableRefObject<ModelViewBridge>;
  /** Phase 3 Session 3.3 (model-view-ux-polish): forwarded to `<ModelLayout>` so the page can hide the sidebar in fullscreen. */
  onFullscreenChange?: (isFullscreen: boolean) => void;
  /** Phase 3 Session 3.4 (model-view-ux-polish): initial UI prefs sourced from `useConversation` in the parent. */
  uiPrefs?: { colWidths?: Record<string, Record<string, number>> };
  /** Phase 3 Session 5: deep-link params + write-back. */
  cellParam?: string;
  namedRangeParam?: string;
  searchParam?: string;
  onConsumeDeepLink?: (params: {
    cell?: boolean;
    namedRange?: boolean;
  }) => void;
  onSearchChange?: (next: string | undefined) => void;
}) {
  const { data, isLoading, error, refetch } = useModelView(conversationId);

  if (!conversationId) {
    // Defensive — the tab is disabled in this case, so we shouldn't render.
    return (
      <div className="flex-1 flex items-center justify-center">
        <span className="text-sm font-mono text-text-muted">
          Start a conversation to see the model.
        </span>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex-1 flex h-full" data-testid="model-view-loading">
        <aside className="w-60 shrink-0 border-r border-panel-border bg-panel-bg flex flex-col gap-2 p-3">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-3 w-32 ml-3" />
          <Skeleton className="h-3 w-28 ml-3" />
        </aside>
        <div className="flex-1 flex flex-col p-6 gap-3">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-3/4" />
          <Skeleton className="h-3 w-2/3" />
        </div>
        <aside className="w-80 shrink-0 border-l border-panel-border bg-panel-bg p-3">
          <Skeleton className="h-3 w-32" />
        </aside>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <EmptyState
          title="Failed to load model"
          description={error instanceof Error ? error.message : String(error)}
          action={
            <Button variant="primary" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  if (!data) {
    // Should be unreachable: `enabled: !!conversationId` + non-loading +
    // no-error implies data is set. Defensive empty state regardless.
    return (
      <div className="flex-1 flex items-center justify-center">
        <span className="text-sm font-mono text-text-muted">
          No model data available.
        </span>
      </div>
    );
  }

  if (data.layoutMissing) {
    return (
      <div
        className="flex-1 flex items-center justify-center"
        data-testid="model-view-layout-missing"
      >
        <LayoutUnavailablePanel
          modelId={data.modelId}
          modelVersion={data.modelVersion}
          latestVersionWithLayout={data.latestVersionWithLayout}
          onCreateNew={onCreateNew}
        />
      </div>
    );
  }

  return (
    <ModelLayout
      data={data}
      conversationId={conversationId}
      onViewInConversation={onViewInConversation}
      bridgeRef={bridgeRef}
      onFullscreenChange={onFullscreenChange}
      uiPrefs={uiPrefs}
      cellParam={cellParam}
      namedRangeParam={namedRangeParam}
      searchParam={searchParam}
      onConsumeDeepLink={onConsumeDeepLink}
      onSearchChange={onSearchChange}
      readOnly={readOnly}
    />
  );
}

/**
 * `<BuilderTabBar>` — Workbench | Signal | Model tab strip rendered above the
 * BuilderToolbar in the main content area. Mirrors `/admin`'s tab styling
 * (mono font, accent active state, panel-border base).
 *
 * Phase 6 Session 3 (2026-05-08) — extended from 2 tabs to 3:
 * - Workbench: today's chat surface (renamed from `'conversation'`).
 * - Signal: new variant-driven dashboard. Disabled when active conversation's
 *   model has `signal_variant === null` (admin hasn't assigned a variant);
 *   tooltip explains the gating.
 * - Model: existing virtualized spreadsheet view. Disabled when no `modelId`
 *   resolves on the active conversation.
 *
 * The schema accepts any of the 3 values without preconditions; disabled
 * states are render-time guards so a stale URL (e.g. `?tab=signal` on a
 * model whose admin hasn't assigned a variant yet) doesn't throw — it lands
 * on a non-clickable tab and the parent route renders the appropriate
 * placeholder body.
 */
const TAB_LABELS: Record<BuilderTab, string> = {
  workbench: 'Workbench',
  signal: 'Signal',
  model: 'Model',
};

function BuilderTabBar({
  activeTab,
  signalDisabled,
  modelDisabled,
  rightSlot,
}: {
  activeTab: BuilderTab;
  signalDisabled: boolean;
  modelDisabled: boolean;
  /**
   * `deal-signal-overhaul` Phase 5 redesign (2026-05-12). Optional
   * right-aligned content rendered on the same row as the tabs. Used by
   * Signal + Model tabs to surface the SEND DEAL LINK button alongside
   * the tab strip (matches Lovable header layout) instead of a separate
   * `signal-model-cta-strip` row below. Workbench leaves it undefined.
   */
  rightSlot?: ReactNode;
}) {
  const navigate = useNavigate();
  return (
    <div
      className="flex items-center justify-between gap-3 px-5 py-2.5 border-b border-panel-border bg-bg shrink-0"
      role="tablist"
      aria-label="Builder view"
    >
      <div className="flex items-center gap-2">
        {BUILDER_TABS.map((t) => {
          const disabled =
            (t === 'model' && modelDisabled) ||
            (t === 'signal' && signalDisabled);
          const tooltip = disabled
            ? t === 'model'
              ? 'Start a conversation to see the model'
              : "This model doesn't have a Signal view configured. Contact admin."
            : undefined;
          return (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={activeTab === t}
              disabled={disabled}
              title={tooltip}
              onClick={() => {
                if (!disabled)
                  navigate({
                    to: '/builder',
                    // Functional `search` merges with current params — preserves
                    // ?conversation=, ?search=, ?cell=, ?namedRange=, ?messageId=,
                    // ?input= across tab clicks. (Pre-fix used a replace `{ tab: t }`
                    // which silently dropped every other param — broke shareable
                    // deep links + lost active conversation context on tab flip.)
                    search: (prev) => ({ ...prev, tab: t }),
                  });
              }}
              data-testid={`builder-tab-${t}`}
              className={clsx(
                // `deal-signal-overhaul` Phase 5 redesign 2026-05-12 — boxed-pill
                // tab style matching the Lovable header (each tab gets a rounded
                // border-box; active state fills with accent-dim + accent border
                // + accent text + subtle glow). Pre-redesign the tabs were
                // underline-style (border-b-2 + -mb-px), which competes with
                // the row's bottom hairline + reads as more app-chrome than
                // selector.
                'px-5 py-1.5 text-xs font-mono tracking-[1.5px] uppercase cursor-pointer transition-all duration-200 border rounded-[2px]',
                activeTab === t
                  ? 'text-accent border-accent bg-accent/[0.06] shadow-[0_0_8px_rgba(0,200,83,0.10)]'
                  : 'text-text-muted hover:text-text border-input-border bg-transparent hover:border-panel-border-hi',
                disabled &&
                  'opacity-40 cursor-not-allowed hover:text-text-muted hover:border-input-border'
              )}
            >
              {TAB_LABELS[t]}
            </button>
          );
        })}
      </div>
      {rightSlot && <div className="flex items-center gap-2">{rightSlot}</div>}
    </div>
  );
}

/**
 * Inline access-denied panel for the admin-view branch when the current user
 * is not an admin. Mirrors the `/admin` page gate style (admin.tsx lines 62-79).
 */
function AccessDeniedInline() {
  const navigate = useNavigate();
  return (
    <div className="flex items-center justify-center h-full">
      <div className="text-center">
        <p className="text-base text-text-muted font-mono mb-3">
          Access Denied
        </p>
        <p className="text-sm text-text-dim font-mono mb-4">
          You need admin privileges to view this page.
        </p>
        <button
          onClick={() => navigate({ to: '/cockpit' })}
          className="px-6 py-3 bg-accent-dim border border-accent/15 rounded-[3px] text-accent font-mono text-sm font-semibold cursor-pointer"
        >
          Go to Cockpit
        </button>
      </div>
    </div>
  );
}

export function BuilderPage() {
  const {
    share: shareParam,
    tab,
    adminView,
    conversation,
  } = useSearch({
    from: '/builder',
  });
  const { isAdmin } = useAuth();

  // Admin read-only branch: admin opens /builder?conversation=<id>&adminView=1
  // from the All Deals admin tab. Renders the REAL AuthModeBuilderPage with
  // editing affordances gated off. (admin-deal-oversight ADR 0007)
  // BEFORE the share branch — adminView takes priority over a hypothetical
  // stale ?share= param on the same URL (defensively unreachable in practice).
  if (adminView && conversation) {
    if (!isAdmin) return <AccessDeniedInline />;
    return <AuthModeBuilderPage adminReadOnly />;
  }

  if (shareParam) {
    return <ReadOnlyShareBuilder shareId={shareParam} activeTab={tab} />;
  }
  return <AuthModeBuilderPage />;
}

function AuthModeBuilderPage({
  adminReadOnly = false,
}: {
  adminReadOnly?: boolean;
}) {
  const { user, isAdmin } = useAuth();
  const {
    tab,
    conversation: conversationFromUrl,
    search: searchParam,
    cell: cellParam,
    namedRange: namedRangeParam,
    messageId: messageIdParam,
    input: inputParam,
    section: sectionParam,
    assumption: assumptionParam,
  } = useSearch({ from: '/builder' });
  const navigate = useNavigate();

  // Initialize from URL `?conversation=<uuid>` (link-sharing + bookmark restore).
  // Invalid uuids resolve to `undefined` via `builderSearchSchema.catch(undefined)`,
  // so this is `null` (no preselection) on a malformed URL and the auto-select
  // effect below picks the first conversation. (model-view-ux-polish Phase 2
  // Session 2.1, 2026-04-27)
  const [activeConversationId, setActiveConversationId] = useState<
    string | null
  >(conversationFromUrl ?? null);
  const [messageHistory, setMessageHistory] = useState<ChatMessage[]>([]);
  const [uiError, setUiError] = useState<string | null>(null);
  // 2026-05-25 — `liveMetrics` SSOT refactor.
  //
  // PRIOR DESIGN (deleted): 4 competing populators — cold-load seeder from
  // `/views.cellValues`, streaming tool-result handler, post-hydration
  // message-history walker, and `metrics:<convId>` localStorage rehydrate.
  // Multiple populators created a race where restore / create / accept
  // version mutations left stale values on the Performance / Hero / Signal
  // panels (the walker re-read pre-restore tool results from message
  // history; the localStorage cache was version-unaware).
  //
  // NEW DESIGN (mirrors `builder-ssot-consolidation` Phase 5's `views.inputs`
  // unification):
  //   1. `liveMetricsCanonical` — pure `useMemo` over `views.cellValues` +
  //      `outputDefinitions`. THE SSOT. Auto-updates on `/views` invalidation
  //      (which fires on: post-stream cache patch, Realtime DiffOp v2
  //      broadcast, `version_changed` broadcast, mutation onSuccess).
  //   2. `inFlightMetrics` — route-local overlay populated ONLY from streaming
  //      SSE `tool_result.recomputed` / `currentMetrics`. Provides
  //      sub-round-trip UX during a run; cleared the moment `/views.cellValues`
  //      refetches (proves canonicalization). Equivalent to the legacy
  //      "streaming optimistic patch" minus the persistent-write semantics.
  //   3. Display `liveMetrics = { ...liveMetricsCanonical, ...inFlightMetrics }`
  //      so streaming overrides always win during a run + the SSOT wins after.
  //
  // Defs land below the `views` hook (which gives us `cellValues`); the
  // overlay state declaration stays here so it's hoisted into render scope.
  const [inFlightMetrics, setInFlightMetrics] = useState<
    Record<string, number>
  >({});
  const [defaultsFilled, setDefaultsFilled] = useState(false);
  // table-interaction-surface (2026-06-09): composer mode machine.
  // `'chat'` = normal ChatInput + defaults row.
  // `'interaction'` = <InteractionSurface> replaces the composer zone.
  // Only one interaction is active at a time per surface.
  const [composerMode, setComposerMode] = useState<'chat' | 'interaction'>(
    'chat'
  );
  const [activeInteraction, setActiveInteraction] =
    useState<InteractionRequest | null>(null);
  // market-default-apply-ux Phase 2 Session 2.4 — stash the full eligibleInputs
  // set (carrying `dataType` + `description`) alongside the active interaction.
  // Threaded to <InteractionSurface eligibleInputs> so the arm body's
  // useDefaultMatchSuggestions hook receives the dataType-aware shape (B2/I1).
  // Cleared in handleInteractionDismiss alongside setActiveInteraction(null).
  type EligibleInput = {
    name: string;
    displayName: string;
    dataType: string;
    description?: string;
  };
  const [activeEligibleInputs, setActiveEligibleInputs] = useState<
    EligibleInput[]
  >([]);
  // `modelGenerated` + `excelGenerating` state REMOVED 2026-05-20 with the
  // generate_excel tool. The "Fill remaining with defaults" CTA used to
  // suppress itself once the user had generated the workbook; now there is
  // no terminal "generated" state — defaults can be filled at any point.
  const [showNewModal, setShowNewModal] = useState(false);
  const [showForkConfirm, setShowForkConfirm] = useState(false);
  const [forkBanner, setForkBanner] = useState<{
    orphanedInputCount: number;
  } | null>(null);
  // Phase 3 Session 3.3 (model-view-ux-polish, 2026-04-27): page-level
  // mirror of `<ModelLayout>`'s `isFullscreen` state. Drives the conditional
  // hide of `<ConversationSidebar>` below. Reset to false whenever the user
  // navigates away from the Model tab — fullscreen is per-route per spec §6.
  // BuilderTabBar + BuilderToolbar always remain visible.
  const [isModelFullscreen, setIsModelFullscreen] = useState(false);
  // builder-ux-overhaul Phase 4 Session 3 — drifted-pill "Restore to active"
  // opens a second <RestoreVersionConfirm> pre-targeted to currentVersionId.
  // Option (a) from the blueprint: local state drives a dedicated mount;
  // no new window event needed.
  const [restoreToActiveOpen, setRestoreToActiveOpen] = useState(false);
  const pendingMessageRef = useRef<string | null>(null);

  const { data: conversationsData } = useConversations();
  const { data: modelsListData, isLoading: modelsLoading } = useModels();
  const { data: conversationDetail, error: conversationError } =
    useConversation(activeConversationId);
  const createConversation = useCreateConversation();
  const forkConversation = useForkConversation();
  const upgradeModelVersion = useUpgradeModelVersion();
  const {
    currentMessage,
    sendMessage,
    status: streamingStatus,
    error: streamingError,
    missingModel: streamingMissing,
    securityBlock,
    retryStatus,
    retriableError,
    retry: retryStreaming,
    abort: abortStreaming,
    clearError: clearStreamingError,
  } = useStreaming();

  const conversations = conversationsData?.data ?? [];
  const detail = conversationDetail?.data;
  const agentState = detail?.agentState ?? null;
  // Phase 2 Session 5 (M5 fix from gap-analysis): refine the opaque
  // `Record<string, unknown>` agentState into the typed shape via safeParse.
  // safeParse (NOT .parse) so a stale agent_state shape on older conversations
  // degrades to `null` rather than throwing — chips render `variant="agent"`
  // with empty popovers (covered by ProvenanceChip's `null inputProvenance`
  // case). Single observer at the route boundary; threaded down through props.
  const agentStateRefined = useMemo(() => {
    if (!agentState) return null;
    const parsed = conversationAgentStateSchema.safeParse(agentState);
    return parsed.success ? parsed.data : null;
  }, [agentState]);

  // Auto-select most recent conversation on first load
  const firstConversationId =
    conversations.length > 0 ? conversations[0].id : null;
  useEffect(() => {
    if (!activeConversationId && firstConversationId) {
      setActiveConversationId(firstConversationId);
    }
  }, [firstConversationId, activeConversationId]);

  // Sync activeConversationId when the URL `?conversation=` param changes after
  // initial mount (e.g. user clicks the "Back to Builder" CTA on the 404 branch
  // → URL drops the param → state should clear so the auto-select effect picks
  // a fresh conversation OR the route renders the no-active state). Without
  // this, state captured from the initial useState(URL) value would stick and
  // the 404 EmptyState would persist after navigation. (model-view-ux-polish
  // Phase 2 Session 2.1, 2026-04-27)
  useEffect(() => {
    if ((conversationFromUrl ?? null) !== activeConversationId) {
      setActiveConversationId(conversationFromUrl ?? null);
    }
    // Intentional: only react to URL changes, not local state changes (those
    // already update both sides via handleSelect).
  }, [conversationFromUrl]); // react-hooks/exhaustive-deps not configured in this project

  // Phase 3 Session 3.3 (model-view-ux-polish, 2026-04-27): reset
  // `isModelFullscreen` whenever the user navigates away from the Model tab.
  // The local `<ModelLayout>` state unmounts naturally with the component;
  // this effect keeps the parent's mirror in sync so the sidebar reappears
  // immediately on tab switch (rather than waiting for the next render).
  useEffect(() => {
    if (tab !== 'model' && isModelFullscreen) {
      setIsModelFullscreen(false);
    }
  }, [tab, isModelFullscreen]);

  // Resolve model ID from active conversation
  const activeConversation = conversations.find(
    (c) => c.id === activeConversationId
  );
  const modelId =
    (agentState?.modelId as string) ?? activeConversation?.modelId ?? null;
  const { data: modelDetail } = useModel(modelId);
  const outputDefinitions = modelDetail?.data?.outputs ?? [];
  const allNamedRanges = modelDetail?.data?.allNamedRanges ?? [];

  // conversation-model-versioning Phase 6 (2026-05-18, ADR-0025): version gate
  // for <SendDealLinkButton>. Hook is called unconditionally (Rules of Hooks)
  // and is disabled when activeConversationId is null (enabled: !!convId inside
  // useConversationVersions). The derived boolean is passed down as `hasVersion`.
  // ADR 0007: version hooks are owner-gated (404 for admin on a foreign deal)
  // and feed version-pill / upgrade affordances that are gated off in admin
  // read-only — pass null to keep them inert (no 404 noise).
  const versionConversationId = adminReadOnly ? null : activeConversationId;
  const { data: conversationVersions } = useConversationVersions(
    versionConversationId
  );
  const hasVersion = (conversationVersions?.length ?? 0) > 0;
  // builder-ux-overhaul Phase 4 Session 1 hook — feeds the ActiveVersionPill
  // (S2) + the restore-to-active confirm (S3) + the share gate (S4).
  const activeVersionDrift = useActiveVersionDrift(versionConversationId);

  // 2026-05-25 fix: the drifted-state pill's "Create new version" button used
  // to dispatch `arcanum:open-versions` with no detail.conversationId payload,
  // so the BrowseVersionsModal's guard silently rejected it. Wire the actual
  // mutation here (sibling of <ManageVersionsButton>'s instance) and thread
  // `onCreateClick` + pending/disabled state down to every pill mount via
  // shared props built below.
  const pillCreateVersion = useCreateVersion(activeConversationId);
  // builder-ux-overhaul Phase 4 Session 4 (ADR-0025 additive share gate):
  // derive inSync from the S1 hook; default false when data unresolved so the
  // share button stays disabled until drift state is confirmed (fail-safe).
  const inSync = activeVersionDrift.data?.inSync ?? false;

  // `/views` is the input SSOT (per builder-ssot-consolidation Phase 5
  // S5.3 + S5.7) — `views.inputs` powers VariablesPanel + DriftBadge + the
  // granular Realtime v2 setQueryData patch. Un-enveloped bare ViewsResponse.
  // It also carries `cellValues` (server-engine computed outputs) which the
  // refactored `liveMetricsCanonical` derivation below treats as the SSOT
  // for OUTPUTS as well. `modelView` is the legacy shim path kept for the
  // Model tab grid + a few other places; both endpoints serve the same
  // cellValues so either works here — we prefer `views.cellValues` because
  // it's reactive to the same query invalidations as VariablesPanel.
  const { data: modelView } = useModelView(activeConversationId);
  const { data: views } = useConversationViews(activeConversationId);
  // Durable server-read of CollectionState — persists the NEXT banner between
  // turns and on load (next-variable-curation Phase 6). The live SSE carrier
  // (`latestToolPayloads.collectionState`) takes precedence while streaming;
  // this is the fallback once the post-turn message refetch clears the live value.
  const { data: serverCollectionState } = useCollectionState(
    activeConversationId ?? undefined
  );

  // SSOT derivation: walk `outputDefinitions` against `views.cellValues` and
  // synthesize `{ outputName: numericValue }`. Re-derives any time the query
  // refetches (post-stream patch, Realtime broadcast, version mutation
  // invalidation, etc.). No race conditions, no localStorage cache, no
  // version-pinned reseed needed.
  //
  // Falls back to `modelView.cellValues` while `views` is still loading
  // (cold-load) — covers the fork / share / cold-page case the old eager
  // seeder protected against. Both endpoints serve identical cellValues
  // shapes from the same server-side engine evaluator.
  const liveMetricsCanonical = useMemo<Record<string, number>>(() => {
    const cells =
      views?.cellValues ??
      (modelView && !('layoutMissing' in modelView)
        ? modelView.cellValues
        : undefined);
    if (!cells || outputDefinitions.length === 0) return {};
    const out: Record<string, number> = {};
    for (const o of outputDefinitions) {
      if (!o.sheet || !o.cell) continue;
      const v = cells[`${o.sheet}!${o.cell}`];
      if (typeof v === 'number' && Number.isFinite(v)) out[o.name] = v;
    }
    return out;
  }, [views?.cellValues, modelView, outputDefinitions]);

  // Display value: streaming overlay wins over canonical so the user sees
  // fresh metrics mid-run before the post-stream cache patch lands. Clears
  // itself in the effect below when `/views.cellValues` refetches with
  // values matching the overlay (proof the in-flight values are canonical).
  const liveMetrics = useMemo<Record<string, number>>(
    () => ({ ...liveMetricsCanonical, ...inFlightMetrics }),
    [liveMetricsCanonical, inFlightMetrics]
  );

  // Drop in-flight overlay keys once the canonical SSOT catches up. Keeps
  // the overlay minimal — overrides only survive until the server-canonical
  // value matches (within a 0.5% tolerance for floating-point noise). The
  // tolerance protects against streaming-emitted values that round-trip
  // through JSON.stringify + DB write + JSON.parse with imperceptible drift.
  useEffect(() => {
    if (Object.keys(inFlightMetrics).length === 0) return;
    const stillNeeded: Record<string, number> = {};
    let changed = false;
    for (const [k, overlayVal] of Object.entries(inFlightMetrics)) {
      const canonical = liveMetricsCanonical[k];
      const matches =
        canonical !== undefined &&
        (canonical === 0
          ? overlayVal === 0
          : Math.abs(canonical - overlayVal) / Math.abs(canonical) < 0.005);
      if (matches) {
        changed = true; // drop this key — canonical caught up
      } else {
        stillNeeded[k] = overlayVal;
      }
    }
    if (changed) setInFlightMetrics(stillNeeded);
  }, [liveMetricsCanonical, inFlightMetrics]);

  // Resolve model name + version for toolbar
  const modelName = modelDetail?.data?.name ?? null;
  const conversationVersion =
    (agentState?.modelVersion as string) ??
    activeConversation?.modelVersion ??
    null;
  // For display: use model detail name. For version: use conversation's locked version.
  const modelsList = modelsListData?.data ?? [];
  const activeModelSummary = modelsList.find(
    (m: { id: string }) => m.id === modelId
  );

  // Fork banner: compare conversation version with latest
  const latestVersion = activeModelSummary?.latestVersion ?? null;
  const showForkBanner = !!(
    activeConversationId &&
    conversationVersion &&
    latestVersion &&
    conversationVersion !== latestVersion
  );

  // Archive completed messages
  const lastArchivedIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      currentMessage &&
      !currentMessage.isStreaming &&
      currentMessage.content &&
      currentMessage.id !== lastArchivedIdRef.current
    ) {
      lastArchivedIdRef.current = currentMessage.id;
      setMessageHistory((prev) => [
        ...prev,
        {
          id: currentMessage.id,
          role: currentMessage.role,
          content: currentMessage.content,
          toolResults: currentMessage.toolResults,
          toolErrors: currentMessage.toolErrors,
          redactedSpans: currentMessage.redactedSpans,
          activeToolCall: null,
          isStreaming: false,
          // Phase 2 Session 4 — persist meta UI state into history so ChatThread
          // re-renders consistently after archive (avoids losing thinking pill,
          // plan checklist, summary card on the first turn after stream completes
          // and before the next sendMessage replaces currentMessage).
          toolCallStates: currentMessage.toolCallStates,
          thinking: currentMessage.thinking,
          plan: currentMessage.plan,
          summary: currentMessage.summary,
        },
      ]);
    }
  }, [currentMessage?.isStreaming, currentMessage?.id]);

  // Stream-into-overlay: fold tool-result metrics into the in-flight overlay
  // (NOT canonical state). The overlay is the "streaming UX hedge" that
  // surfaces fresh values before the post-stream `/views` cache patch lands.
  // The overlay-clear effect above drops entries once canonical catches up,
  // so the SSOT remains authoritative for cross-tab + cross-mutation cases.
  //
  // Legacy: `recomputed` is the Phase 2 universal state-delta populated by
  // `collectStateDelta` on every state-mutating tool (set_inputs,
  // set_table_inputs, apply_market_defaults, accept_defaults). `currentMetrics`
  // is the older legacy shape — both folded for back-compat. Bug surfaced
  // 2026-05-06 (Las Vegas conversation aed6f3b7 / 3c2b3817): panel got stuck
  // on prior tool's `currentMetrics` while engine reflected the next tool's
  // `recomputed`. Order here matters — `recomputed` wins because it's
  // canonical post-mutation state.
  useEffect(() => {
    if (!currentMessage?.toolResults?.length) return;
    for (const tr of currentMessage.toolResults) {
      const result = tr.result as Record<string, unknown> | undefined;
      if (!result) continue;

      if (result.currentMetrics && typeof result.currentMetrics === 'object') {
        setInFlightMetrics((prev) => ({
          ...prev,
          ...(result.currentMetrics as Record<string, number>),
        }));
      }
      if (result.recomputed && typeof result.recomputed === 'object') {
        setInFlightMetrics((prev) => ({
          ...prev,
          ...(result.recomputed as Record<string, number>),
        }));
      }

      if (Array.isArray(result.outputs)) {
        const metricsFromOutputs: Record<string, number> = {};
        for (const o of result.outputs as Array<{
          name: string;
          value: unknown;
        }>) {
          if (typeof o.value === 'number') metricsFromOutputs[o.name] = o.value;
        }
        if (Object.keys(metricsFromOutputs).length > 0) {
          setInFlightMetrics((prev) => ({ ...prev, ...metricsFromOutputs }));
        }
      }

      if (
        result.values &&
        typeof result.values === 'object' &&
        !Array.isArray(result.values)
      ) {
        const metricsFromGet: Record<string, number> = {};
        for (const [key, info] of Object.entries(
          result.values as Record<string, { value: unknown }>
        )) {
          if (typeof info?.value === 'number') metricsFromGet[key] = info.value;
        }
        if (Object.keys(metricsFromGet).length > 0) {
          setInFlightMetrics((prev) => ({ ...prev, ...metricsFromGet }));
        }
      }
    }
  }, [currentMessage?.toolResults?.length]);

  // Reset excel generating when streaming ends
  const queryClient = useQueryClient();
  useEffect(() => {
    // `setExcelGenerating(false)` reset on streaming-end REMOVED 2026-05-20
    // with the generate_excel tool. No replacement needed.
    if (streamingStatus === 'done' && activeConversationId) {
      // Phase 3-3d: Optimistic cache patch — apply tool-result state-deltas
      // into the cached agentState BEFORE the server refetch lands.
      //
      // The lag between `done` and the VariablesPanel showing new rows is the
      // round-trip for `refetchQueries` (already fired by use-streaming-v2.ts).
      // By merging `currentProvenance` + `inputValues` from tool results here,
      // VariablesPanel re-renders immediately — the subsequent refetch lands and
      // overwrites the optimistic patch with the authoritative server state.
      //
      // Contract: ONLY patch `inputProvenance` and `inputValues`. Never touch
      // `dealMetadata`, `metricHistory`, `aerialLatLng`, or `orphanedInputs`
      // (the 4-key preservation contract from cockpit-lovable-alignment Phase 2
      // Session 2.4 / conversation-model-versioning F11) — server is
      // authoritative for those fields and patching them optimistically risks
      // clobbering post-stream out-of-band writes (e.g. geocodeAndCache).
      //
      // State-mutating tools that populate stateDelta:
      //   set_inputs: written={namedRange:value}, currentProvenance={namedRange:{...}}
      //   set_table_inputs: written has composite ${table}.${row}.${col} keys
      //     (NOT raw named-range keys) — skip inputValues for table cells;
      //     currentProvenance is empty (currentProvenanceFor:[]) — no patch possible.
      //   apply_market_defaults / accept_defaults: currentProvenance carries
      //     per-field provenance objects.
      //
      // Hidden tool filter: propose_plan / propose_table_fill / market_data_enrichment
      // do not mutate agent_state — skip if they have no currentProvenance.
      const toolResults = currentMessage?.toolResults ?? [];
      if (toolResults.length > 0) {
        const mergedInputValues: Record<string, unknown> = {};
        const mergedInputProvenance: Record<string, unknown> = {};
        let hasUpdates = false;

        for (const tr of toolResults) {
          const result = tr.result as
            | Record<string, unknown>
            | null
            | undefined;
          if (!result) continue;

          // Merge inputValues from `written` for set_inputs (raw named-range keys).
          // Exclude set_table_inputs composite keys (${table}.${row}.${col}).
          if (
            tr.tool === 'set_inputs' &&
            result.written &&
            typeof result.written === 'object' &&
            !Array.isArray(result.written)
          ) {
            for (const [k, v] of Object.entries(
              result.written as Record<string, unknown>
            )) {
              mergedInputValues[k] = v;
              hasUpdates = true;
            }
          }

          // Merge inputProvenance from `currentProvenance` for any tool that
          // populates it (set_inputs, apply_market_defaults, accept_defaults).
          if (
            result.currentProvenance &&
            typeof result.currentProvenance === 'object' &&
            !Array.isArray(result.currentProvenance)
          ) {
            for (const [k, v] of Object.entries(
              result.currentProvenance as Record<string, unknown>
            )) {
              if (v && typeof v === 'object') {
                mergedInputProvenance[k] = v;
                hasUpdates = true;
              }
            }
          }
        }

        if (hasUpdates) {
          queryClient.setQueryData(
            queryKeys.conversations.detail(activeConversationId),
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (prev: any) => {
              if (!prev?.data?.agentState) return prev;
              const prevAgentState = prev.data.agentState as Record<
                string,
                unknown
              >;
              return {
                ...prev,
                data: {
                  ...prev.data,
                  agentState: {
                    ...prevAgentState,
                    inputValues: {
                      ...(prevAgentState.inputValues as
                        | Record<string, unknown>
                        | undefined),
                      ...mergedInputValues,
                    },
                    inputProvenance: {
                      ...(prevAgentState.inputProvenance as
                        | Record<string, unknown>
                        | undefined),
                      ...mergedInputProvenance,
                    },
                  },
                },
              };
            }
          );
        }
      }

      queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.detail(activeConversationId),
      });
      // Invalidate the durable collection-state so the NEXT banner reflects
      // the just-finished turn's mutations (next-variable-curation Phase 6).
      queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.collectionState(activeConversationId),
      });
    }
  }, [streamingStatus, activeConversationId, queryClient, currentMessage]);

  // -------------------------------------------------------------------------
  // Realtime channel wiring — realtime-collab-foundation Phase 4 Session 4.
  //
  // Every committed edit (local or remote) arrives as a DiffOp broadcast on
  // `conversation:<id>`. The hook's callbacks invalidate the conversation-
  // detail query so `useConversation` refetches the fresh agent_state
  // projection — VariablesPanel then re-renders automatically via prop
  // change.
  //
  // Design choice (per blueprint): invalidate-and-refetch, NOT locally merge
  // `computed_changes` into the cached agentState. Rationale: the
  // agent_state projection is maintained TRANSACTIONALLY alongside every
  // recordEdit() on the server (see Phase 1 design), so a single invalidate
  // pulls the authoritative state in one round-trip. Duplicating the merge
  // logic client-side would risk divergence from the server's normalization
  // (e.g. "5%" -> 0.05) and downstream formula recalculation.
  // -------------------------------------------------------------------------
  const initialRevision =
    typeof (
      detail as unknown as { currentRevision?: number } | null | undefined
    )?.currentRevision === 'number'
      ? (detail as unknown as { currentRevision: number }).currentRevision
      : 0;

  // `void`-prefix the return values — none of `realtimeStatus`,
  // `confirmedRevision`, or `otherTabsCount` surface in Phase 4 UI. They're
  // available for later features (e.g. an observability status pill or
  // presence indicators) and for debugging via React DevTools.
  // Phase 4 Session 4 (in-platform-model-view): route-level bridge ref for
  // Realtime DiffOp fan-out into the Model tab's local `cellValues` cache.
  // `<ModelLayout>` registers `applyDiff` + `reset` into `bridgeRef.current`
  // on mount; the single `useConversationChannel` mount below fans out
  // every inbound DiffOp to BOTH (a) the existing TanStack Query
  // invalidation (drives VariablesPanel re-render) and (b) the bridge ref
  // (updates the grid in place). Single-mount invariant preserved.
  const modelViewBridgeRef = useRef<ModelViewBridge>({});

  const { realtimeStatus, syncStatus, confirmedRevision, otherTabsCount } =
    // ADR 0007: admin live oversight — admins SUBSCRIBE to the owner's channel
    // read-only (silent observer; the web hook never publishes presence). The
    // realtime.messages subscribe RLS + the /diffs+/resync read-override admit
    // admins, so live edits stream into the read-only Builder.
    useConversationChannel(activeConversationId, {
      initialRevision,
      onDiffApplied: (diff) => {
        if (!activeConversationId) return;
        debugRealtime('onDiffApplied', {
          editId: diff.editId,
          revision: diff.revision,
          conversationId: activeConversationId,
        });

        // builder-ssot-consolidation Phase 5 Session 5.3 (ADR 0017):
        // v2 diffs (Realtime broadcast path) carry `provenanceDeltas` instead
        // of `appliedOps`. Patch the views cache granularly so consumers
        // (VariablesPanel, Signal, DriftBadge) update without a full refetch.
        // v1 diffs (polling/resync path) fall through to the legacy invalidation.
        if ('provenanceDeltas' in diff) {
          const v2 = diff as DiffOpV2;
          // Granular patch into the views cache — one entry per touched key.
          queryClient.setQueryData(
            queryKeys.conversations.views(activeConversationId),
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (prev: any) => {
              if (!prev) return prev;
              const nextInputs = { ...prev.inputs };
              for (const delta of v2.provenanceDeltas) {
                nextInputs[delta.key] = {
                  ...(nextInputs[delta.key] ?? {}),
                  value: delta.valueAfter,
                  source: delta.source,
                  ...(delta.defaultStatus !== undefined
                    ? { defaultStatus: delta.defaultStatus }
                    : {}),
                  ...(delta.reasoning !== undefined
                    ? { reasoning: delta.reasoning }
                    : {}),
                };
              }
              const nextCellValues = { ...prev.cellValues };
              for (const change of v2.computedChanges) {
                nextCellValues[`${change.sheet}!${change.address}`] =
                  change.newValue;
              }
              return {
                ...prev,
                inputs: nextInputs,
                cellValues: nextCellValues,
              };
            }
          );
          // Also invalidate the detail cache so useConversation shim overlay
          // picks up the fresh views data on next render.
          //
          // 2026-06-09 — `exact: true`: `detail(id)` is the PREFIX of the
          // nested `views` / `active-version-sync` / `model-view` / `versions`
          // keys (see query-keys.ts), so a non-exact invalidation fans every
          // inbound v2 diff into a refetch of ALL of them — the cascade that
          // tripped the 120/min per-conversation reads limiter. The views
          // cache was just patched granularly above (setQueryData); only the
          // detail-shim leaf needs to re-read. Mirrors the leaf-scoped resync
          // + polling invalidation in use-conversation-channel.ts.
          queryClient.invalidateQueries({
            queryKey: queryKeys.conversations.detail(activeConversationId),
            exact: true,
          });
          return;
        }

        // v1 path removed (ADR-0021 Option B): polling/resync catch-up now
        // invalidates via the channel hook directly (S7.3). This arm is
        // unreachable — onDiffApplied only fires for v2 Realtime broadcasts.
      },
      onSnapshotApplied: () => {
        if (!activeConversationId) return;
        debugRealtime('onSnapshotApplied', {
          conversationId: activeConversationId,
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.conversations.detail(activeConversationId),
        });
        // Snapshot reset: blow away the Model tab's local cache + force a
        // fresh `/model-view` refetch so the grid reseeds from the
        // authoritative server projection. Also invalidate the views cache
        // (builder-ssot-consolidation Phase 5 Session 5.3 — ADR 0017).
        modelViewBridgeRef.current.reset?.();
        queryClient.invalidateQueries({
          queryKey: queryKeys.conversations.modelView(activeConversationId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.conversations.views(activeConversationId),
        });
      },
      // 2026-05-25 — cross-tab convergence on create/restore/accept.
      // Server-side `publishVersionChanged` (conversations.service.v2.ts)
      // emits this on every version mutation. The hook already invalidated
      // `conversations.all` (which triggers `/views` refetch → canonical
      // metrics re-derive automatically); here we additionally clear the
      // streaming overlay so any in-flight pre-mutation values disappear
      // immediately. No localStorage cleanup needed — that cache was
      // deleted in the SSOT refactor.
      onVersionChanged: (detail) => {
        if (!activeConversationId) return;
        if (detail.conversationId !== activeConversationId) return;
        debugRealtime('onVersionChanged', detail);
        setInFlightMetrics({});
      },
    });
  // realtimeStatus is consumed by <BuilderToolbar> below to render the
  // <RealtimeStatusBadge> when the channel is not 'connected'.
  // (model-view-ux-polish Phase 2 Session 2.2, 2026-04-27)
  void confirmedRevision;
  void otherTabsCount;

  // Build message list
  const isCurrentlyStreaming =
    currentMessage?.isStreaming &&
    currentMessage.id !== lastArchivedIdRef.current;
  const allMessages: ChatMessage[] = isCurrentlyStreaming
    ? [
        ...messageHistory,
        {
          id: currentMessage.id,
          role: currentMessage.role,
          content: currentMessage.content,
          toolResults: currentMessage.toolResults,
          toolErrors: currentMessage.toolErrors,
          redactedSpans: currentMessage.redactedSpans,
          activeToolCall: currentMessage.activeToolCall,
          isStreaming: true,
          // Phase 2 Session 4 — surface meta UI state to ChatThread.
          toolCallStates: currentMessage.toolCallStates,
          thinking: currentMessage.thinking,
          plan: currentMessage.plan,
          summary: currentMessage.summary,
        },
      ]
    : messageHistory;

  // Hydrate messageHistory from conversation detail. Initial load seeds the
  // full list; subsequent refetches (e.g. after a `/model-edit` commit
  // triggers `invalidateQueries(detail(id))` via the route-level fan-out, OR
  // after the conversation-stream `done` event invalidates) merge new
  // messages by id so post-edit SystemMessages flow through to
  // `<ChatThread>` for Phase 4 Session 4 grouping.
  //
  // Local-stub IDs (`human-${Date.now()}` from handleSend, `msg-${Date.now()}`
  // from use-streaming-v2) NEVER match the server's UUID-based message IDs.
  // Drop them before merging — the server's persistent versions in `incoming`
  // are the authoritative replacement. Pre-fix, dedupe-by-id missed the
  // local stubs, both copies survived, and `<ChatThread>` rendered 4 bubbles
  // instead of 2 after a single agent turn (race-dependent — passed when the
  // detail refetch landed after the smoke spec's `toHaveCount(2)` assertion;
  // failed when it landed before). Surfaced by the v1 variant of the
  // `automated-e2e-validation` harness spec on 2026-05-01.
  useEffect(() => {
    if (!detail?.messages || detail.messages.length === 0) return;
    const incoming: ChatMessage[] = detail.messages.map((m) => ({
      id: m.id,
      role: m.role,
      content: m.content,
      created_at: m.createdAt || undefined,
      tableSnapshot: m.tableSnapshot,
      toolResults: m.toolResults as ChatMessage['toolResults'],
      toolErrors: m.toolErrors as ChatMessage['toolErrors'],
      // agent-ux-optimization Phase 1 wire-fix (2026-05-07) — propagate
      // `toolCallStates` from `useConversation`'s `deriveToolCallStates`
      // hydration. Without this, `<ChatThread>`'s receipt-grouping gate
      // (`m.role === 'ai' && m.toolCallStates && m.toolCallStates.size > 0`)
      // never fires for hydrated messages, so reload + revisit on a
      // conversation with completed tool calls renders empty.
      toolCallStates: (m as { toolCallStates?: Map<string, unknown> })
        .toolCallStates as ChatMessage['toolCallStates'],
      isStreaming: false,
    }));
    setMessageHistory((prev) => {
      // Pre-Phase-1-wire-fix (2026-05-07): the dedupe must DROP local
      // `msg-*` / `human-*` stub IDs (they never match server UUIDs), but
      // it also must PRESERVE the streaming-only fields (summary, plan,
      // thinking, toolCallStates) that the server's `getMessages` projection
      // doesn't carry. Walk the local stubs in order and pair each with the
      // next un-paired AI/human server message of matching role; copy the
      // streaming-only fields onto the server message before insertion.
      const localStubs = prev.filter(
        (m) => m.id.startsWith('human-') || m.id.startsWith('msg-')
      );
      const persistent = prev.filter(
        (m) => !m.id.startsWith('human-') && !m.id.startsWith('msg-')
      );
      const existingIds = new Set(persistent.map((m) => m.id));
      const newOnes = incoming.filter((m) => !existingIds.has(m.id));

      // Pair local stubs with server messages by sequential role-match.
      // The agent's `getMessages` returns messages in append-only LangGraph
      // order, so iterating both in order and pairing by role is reliable
      // for the common single-user-turn-then-single-AI-turn pattern. For
      // multi-message agent turns (rare), the LAST stub's fields land on
      // the LAST matching server message — acceptable since the streaming
      // hook only ever produces ONE in-flight `currentMessage` per turn.
      let stubCursor = 0;
      const enriched = newOnes.map((server) => {
        if (stubCursor >= localStubs.length) return server;
        const stub = localStubs[stubCursor];
        if (stub.role !== server.role) return server;
        stubCursor++;
        // Streaming-only fields: forward only when the stub carried them
        // (avoids clobbering a future server-side projection if `getMessages`
        // is later extended to surface these directly).
        return {
          ...server,
          ...(stub.summary !== undefined ? { summary: stub.summary } : {}),
          ...(stub.plan !== undefined ? { plan: stub.plan } : {}),
          ...(stub.thinking !== undefined ? { thinking: stub.thinking } : {}),
          ...(stub.toolCallStates !== undefined
            ? { toolCallStates: stub.toolCallStates }
            : {}),
          ...(stub.redactedSpans !== undefined && stub.redactedSpans.length > 0
            ? { redactedSpans: stub.redactedSpans }
            : {}),
        } as ChatMessage;
      });

      // Order-preserving merge: persistent server-IDs come first (they were
      // already in the in-order list); enriched newOnes appended in insertion
      // order from `incoming` (which the API guarantees is LangGraph's
      // append-only `messages` channel order).
      const merged = [...persistent, ...enriched];

      // Bug 2 dedupe (2026-05-10): within each AI message's toolCallStates
      // Map, drop legacy-prefixed entries when a real `tool_call_id` entry
      // exists for the same tool. The streaming hook keys by real
      // tool_call_ids; hydration via `deriveToolCallStates` synthesizes
      // `legacy-${messageId}-${idx}` IDs because `getMessages` doesn't
      // surface tool_call_id today. If both sets coexist on one message
      // (e.g. a hydration race on the 5s/30s delayed refetch), the receipt
      // grouping in <ChatThread> renders the SAME tool's card twice.
      // Defensive fix: prefer real tool_call_ids; drop legacy entries
      // for tools that already have a real-id entry. Cold-load (no
      // streaming context) keeps legacy IDs because no real ones exist.
      return merged.map((m) => {
        if (m.role !== 'ai' || !m.toolCallStates || m.toolCallStates.size === 0)
          return m;
        const realIdsByTool = new Set<string>();
        for (const [id, state] of m.toolCallStates.entries()) {
          if (!id.startsWith('legacy-')) realIdsByTool.add(state.tool);
        }
        if (realIdsByTool.size === 0) return m;
        const deduped = new Map<
          string,
          typeof m.toolCallStates extends Map<string, infer V> ? V : never
        >();
        for (const [id, state] of m.toolCallStates.entries()) {
          if (id.startsWith('legacy-') && realIdsByTool.has(state.tool))
            continue;
          deduped.set(id, state);
        }
        return deduped.size === m.toolCallStates.size
          ? m
          : { ...m, toolCallStates: deduped };
      });
    });

    // 2026-05-25 — Post-hydration message-history walker DELETED. The walker
    // re-derived `liveMetrics` from the LAST persisted tool result in chat
    // history, which competed with the cold-load seeder and (critically)
    // resurrected pre-restore values after a version restore (since restore
    // doesn't remove the prior tool-result messages). Replaced by the
    // `liveMetricsCanonical` useMemo (~line 595) which derives from
    // `views.cellValues` — the server-engine SSOT — and auto-refetches on
    // version mutations + Realtime broadcasts. No data lost: the SSOT
    // includes everything the walker recovered, fresher and version-safe.
  }, [detail?.messages]);

  // 2026-05-25 — `metrics:<convId>` localStorage persist + rehydrate DELETED.
  // The cache was version-unaware (held v1 values after restore to v2 etc.)
  // and competed with the canonical derivation, producing the stale-Performance
  // bug surfaced 2026-05-25. The canonical SSOT (`views.cellValues`) refetches
  // fast enough on conversation-switch that the brief Skeleton + empty render
  // is preferable to silently rendering stale values.

  // Reset state when switching conversations
  const handleSelect = useCallback(
    (id: string) => {
      if (id === activeConversationId) return;
      setActiveConversationId(id);
      // Keep URL in sync so refresh / share-link / browser-history reflects the
      // active conversation. Preserves the current `tab` value. Replace (not
      // push) so back-button doesn't accumulate one entry per sidebar click.
      // (model-view-ux-polish Phase 2 Session 2.1, 2026-04-27)
      navigate({
        to: '/builder',
        search: { tab, conversation: id },
        replace: true,
      });
      setMessageHistory([]);
      setUiError(null);
      // Clear streaming overlay only — canonical metrics re-derive from
      // `/views.cellValues` of the just-selected conversation.
      setInFlightMetrics({});
      setDefaultsFilled(false);
      // setModelGenerated/setExcelGenerating REMOVED 2026-05-20 (generate_excel sunset)
    },
    [activeConversationId, navigate, tab]
  );

  const inputProvenance =
    (agentState?.inputProvenance as
      | Record<
          string,
          {
            source?: string;
            defaultStatus?: string | null;
            derivedFrom?: string | null;
          }
        >
      | undefined) ?? {};
  // Total-inputs denominator: the model's full scalar-input count from the
  // schema — `modelDetail?.data?.inputs?.length`. Pre-`modelDetail` early
  // render falls through to 0 (renders `0/0`), avoiding a flash of a wrong
  // number that would change once the schema resolves.
  //
  // Per-model awareness: `modelDetail?.data?.inputs?.length` is per-(modelId,
  // modelVersion). NEVER hardcode a model-specific integer — a `|| 43`
  // sentinel caused per-model counter drift on non-multifamily-v1
  // conversations (fixed by operating-asset-fixes Session 3, 2026-04-19).
  //
  // `Object.keys(inputValues).length` is NOT a valid denominator source — it
  // counts how many inputs are CURRENTLY SET, which is monotonic during a
  // session but always ≤ totalInputs and would flip the denominator down to
  // match the numerator on first edit (the `1/1` bug fixed by
  // model-view-ux-polish Phase 1 Session 1.3, 2026-04-27).
  //
  // variables-counter-coherence Inc-3 (2026-05-20): `totalInputs` is the
  // canonical denominator = scalar inputs + sum of all table cells. Matches
  // the agents-side `IngestionResult.addressableInputCount` formula AND the
  // `model_registry.addressable_input_count` column populated at ingest.
  // Pre-fix used `schema.inputs.length` alone (43 for mfv1), which UNDER-
  // counted whenever the model had tables — leading to the `50/43` overflow
  // on MODEL OVERVIEW after accept_defaults (numerator counted table cells
  // via the `collected` predicate's provenance walk, denominator did not).
  // Web-derived (not server-read) so it works even when
  // `addressable_input_count IS NULL` on pre-re-ingest registry rows.
  const scalarInputCount = modelDetail?.data?.inputs?.length ?? 0;
  const tableInputs = modelDetail?.data?.tableInputs ?? [];
  const tableCellCount = tableInputs.reduce(
    (sum, t) => sum + t.rows.length * t.columns.length,
    0
  );
  const totalInputs = scalarInputCount + tableCellCount;
  // builder-ux-overhaul FEATURE_END (2026-05-22): names of the schema's scalar
  // inputs, used to keep geocode-derived `Location.*` sub-attrs OUT of the
  // `collected` numerator. Those sub-attrs (source 'market_data', derivedFrom
  // set, NOT a schema input) are absent from the `totalInputs` denominator, so
  // counting them inflated `collected` past `totalInputs` (the 71/68 inversion).
  // Mirrors the VariablesPanel DERIVED-bucket discriminator (ADR 0005).
  const scalarInputNames = new Set(
    (modelDetail?.data?.inputs ?? []).map((i) => i.name)
  );
  const collected = Object.entries(inputProvenance).filter(([name, p]) => {
    if (!p) return false;
    // Exclude read-only derived geocode sub-attrs (DERIVED bucket) — not
    // addressable schema inputs, not in the denominator. Match either an
    // explicit `derivedFrom` (server now stamps it) OR a namespaced key
    // (`Location.coordinates`, …) for a non-user, non-schema entry — defense if
    // any write path still omits `derivedFrom`. builder-ux-overhaul FEATURE_END.
    if (
      p.source !== 'user' &&
      (p.derivedFrom != null || name.includes('.')) &&
      !scalarInputNames.has(name)
    )
      return false;
    if (p.source === 'user') return true;
    if (p.source === 'market_data') return true;
    // ADR-0009 / 2026-05-18 decision: widen from the old `=== 'accepted'`-only
    // gate to the full addressed set — mirrors `isAddressedStandard` on the
    // server (completeness-gate.service.ts) and `ADDRESSED_DEFAULT_STATUSES` in
    // lib/version-completeness.ts.
    // 'accepted'   — legacy status seeded pre-2026-05-18 (old accept_defaults tool)
    // 'confirmed'  — NEW status written by accept_defaults tool (2026-05-18+)
    // 'overridden' — written when a user value or market-data value replaces a default
    // A null/undefined defaultStatus (fresh-seeded unreviewed default) is NOT collected.
    if (
      p.source === 'default' &&
      (p.defaultStatus === 'accepted' ||
        p.defaultStatus === 'confirmed' ||
        p.defaultStatus === 'overridden')
    )
      return true;
    return false;
  }).length;
  // Defensive clamp: schema-shrink during a mid-conversation model-version
  // migration could leave `collected > totalInputs`. Clamp to 0 so the
  // counter never displays a negative pending value.
  const pending = Math.max(0, totalInputs - collected);

  // conversation-model-versioning Phase 5 — single readiness compute at the
  // route boundary. Drives the honest defaults-bar copy below (Fix C): a
  // required, unresolved property location is NOT a "using defaults" input
  // (it is must-select — not defaultable), so it must not be lumped into the
  // generic pending bucket. `<ManageVersionsButton>` recomputes internally
  // from the same raw inputs (same pure fn) — kept independent to avoid
  // threading a fifth prop down to the tab-row dropdown.
  const schemaInputDefs = modelDetail?.data?.inputs;
  const routeDealMetadata =
    (agentState?.dealMetadata as
      | ({ market?: string | null } & Record<string, unknown>)
      | null
      | undefined) ?? null;
  const routeOrphanedInputs =
    (agentState?.orphanedInputs as Record<string, unknown> | undefined) ?? {};
  const versionReadiness =
    Array.isArray(schemaInputDefs) && schemaInputDefs.length > 0
      ? computeVersionReadiness(
          schemaInputDefs,
          inputProvenance as unknown as Record<
            string,
            ProvenanceEntry | string | undefined
          >,
          Object.fromEntries(
            schemaInputDefs
              .filter(
                (
                  inp
                ): inp is typeof inp & {
                  dataType: NonNullable<typeof inp.dataType>;
                } => inp.dataType != null
              )
              .map((inp) => [inp.name, inp.dataType])
          ),
          routeDealMetadata,
          routeOrphanedInputs
        )
      : { ready: false, unaddressed: [] };
  const locationRequiredUnresolved = versionReadiness.unaddressed.some(
    (u) => u.isLocation
  );

  // builder-ux-overhaul Phase 4 Session 3: hoist the blockingReason IIFE so
  // it is computed once and shared by BOTH <BrowseVersionsModal> AND the
  // second <RestoreVersionConfirm> mount (per blueprint "prefer hoisting").
  const unaddressedNames = versionReadiness.unaddressed.map((u) =>
    u.isLocation ? 'Property location — not yet resolved' : u.displayName
  );
  const blockingReasonPreview = unaddressedNames.slice(0, 3).join(', ');
  const blockingReasonOverflow =
    unaddressedNames.length > 3
      ? ` (+${unaddressedNames.length - 3} more)`
      : '';
  const restoreBlockingReason =
    unaddressedNames.length > 0
      ? `Address these inputs first: ${blockingReasonPreview}${blockingReasonOverflow}`
      : '';

  // 2026-05-25 fix: shared props passed to every <ActiveVersionPill> mount
  // (4 sites) so the drifted-state "Create new version" button calls the
  // route-level `useCreateVersion` mutation. The empty-args mutate() mirrors
  // <ManageVersionsButton>'s shape — server picks the v<seq> label.
  const pillCreateProps = {
    onCreateClick: activeConversationId
      ? () => pillCreateVersion.mutate({})
      : undefined,
    createPending: pillCreateVersion.isPending,
    createDisabled: !versionReadiness.ready,
    createBlockedReason: restoreBlockingReason,
  } as const;

  const initials = user?.email?.slice(0, 2).toUpperCase() || 'AA';
  // `modelAlreadyGenerated` REMOVED 2026-05-20: generate_excel sunset means
  // no future conversation will set `agentState.generatedExcelUrl`. Historical
  // conversations may still have a URL stored, but it no longer gates UI.

  // Phase 3 Session 5: deep-link callbacks. `consumeDeepLink` is invoked by
  // ModelLayout after a successful (or failed-but-logged) `?cell=` /
  // `?namedRange=` resolution; we drop the URL param so the effect doesn't
  // re-fire on subsequent renders (e.g. unrelated state changes that
  // refresh the route tree). `handleSearchChange` is the TreeNavigator's
  // 250ms-debounced write-back.
  // Snapshot current URL state in a ref so the deep-link callbacks below
  // (`consumeDeepLink`, `handleSearchChange`, message-id timeout, input
  // dispatch) can build a literal search object without falling into the
  // reducer-fn form's typing pitfall (TanStack Router widens `prev` to
  // the cross-route search union, which loses the `tab` literal-string
  // narrowing the route requires).
  const currentSearchRef = useRef({
    tab,
    conversation: conversationFromUrl,
    search: searchParam,
    cell: cellParam,
    namedRange: namedRangeParam,
    messageId: messageIdParam,
    input: inputParam,
    section: sectionParam,
    assumption: assumptionParam,
  });
  useEffect(() => {
    currentSearchRef.current = {
      tab,
      conversation: conversationFromUrl,
      search: searchParam,
      cell: cellParam,
      namedRange: namedRangeParam,
      messageId: messageIdParam,
      input: inputParam,
      section: sectionParam,
      assumption: assumptionParam,
    };
  }, [
    tab,
    conversationFromUrl,
    searchParam,
    cellParam,
    namedRangeParam,
    messageIdParam,
    inputParam,
    sectionParam,
    assumptionParam,
  ]);

  const consumeDeepLink = useCallback(
    (params: { cell?: boolean; namedRange?: boolean }) => {
      const cur = currentSearchRef.current;
      navigate({
        to: '/builder',
        search: {
          ...cur,
          cell: params.cell ? undefined : cur.cell,
          namedRange: params.namedRange ? undefined : cur.namedRange,
        },
        replace: true,
      });
    },
    [navigate]
  );

  // Phase 6 Session 8 — Signal anchor consume. Drops `?section=` /
  // `?assumption=` from the URL after the SignalAnchorListener has fired
  // its scroll/flash side effects. Without this, the listener re-fires
  // on every unrelated state change.
  const consumeSignalAnchor = useCallback(() => {
    const cur = currentSearchRef.current;
    if (!cur.section && !cur.assumption) return;
    navigate({
      to: '/builder',
      search: { ...cur, section: undefined, assumption: undefined },
      replace: true,
    });
  }, [navigate]);

  const handleSearchChange = useCallback(
    (next: string | undefined) => {
      const cur = currentSearchRef.current;
      navigate({
        to: '/builder',
        search: { ...cur, search: next },
        replace: true,
      });
    },
    [navigate]
  );

  // `?messageId=` deep link: MutationObserver waits up to 5s for a DOM
  // node carrying `[data-message-id="<uuid>"]` to mount, then scrolls it
  // into view + flashes a 800ms accent border. Silent timeout fallback.
  // Effect runs once per param value; dropping the URL param after either
  // resolution OR timeout prevents re-fire.
  useEffect(() => {
    if (!messageIdParam) return undefined;
    let resolved = false;
    const timeoutMs = 5000;
    const flashMs = 800;
    const dropParam = () => {
      const cur = currentSearchRef.current;
      navigate({
        to: '/builder',
        search: { ...cur, messageId: undefined },
        replace: true,
      });
    };
    const tryFlash = (target: Element): void => {
      if (resolved) return;
      resolved = true;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.setAttribute('data-flash-state', 'on');
      window.setTimeout(() => {
        target.removeAttribute('data-flash-state');
        dropParam();
      }, flashMs);
    };
    const initial = document.querySelector(
      `[data-message-id="${messageIdParam}"]`
    );
    if (initial) {
      tryFlash(initial);
      return undefined;
    }
    const observer = new MutationObserver(() => {
      const found = document.querySelector(
        `[data-message-id="${messageIdParam}"]`
      );
      if (found) tryFlash(found);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    const timeoutHandle = window.setTimeout(() => {
      if (!resolved) {
        resolved = true;
        observer.disconnect();

        console.warn('[deep-link-resolution]', {
          surface: 'workbench',
          reason: 'message_id_timeout',
          param: messageIdParam,
          deep_link_resolution_failed: true,
        });
        try {
          window.dispatchEvent(
            new CustomEvent('arcanum:deep-link-resolution', {
              detail: {
                surface: 'workbench',
                reason: 'message_id_timeout',
                param: messageIdParam,
                deep_link_resolution_failed: true,
              },
            })
          );
        } catch {
          // Defensive — env without CustomEvent.
        }
        dropParam();
      }
    }, timeoutMs);
    return () => {
      observer.disconnect();
      window.clearTimeout(timeoutHandle);
    };
  }, [messageIdParam, navigate]);

  // `?input=` deep link: dispatch the `arcanum:scroll-to-input` event
  // that VariablesPanel listens for. Silent if the panel isn't mounted
  // (e.g. user hits the URL while on the Model tab — the event fires
  // when they switch to Conversation tab and the panel re-mounts).
  useEffect(() => {
    if (!inputParam) return;
    try {
      window.dispatchEvent(
        new CustomEvent('arcanum:scroll-to-input', {
          detail: { namedRange: inputParam },
        })
      );
    } catch {
      // Defensive — env without CustomEvent.
    }
    const cur = currentSearchRef.current;
    navigate({
      to: '/builder',
      search: { ...cur, input: undefined },
      replace: true,
    });
  }, [inputParam, navigate]);

  // Phase 3 Session 4: derive the latest `nextSuggestion` (from set_inputs)
  // and `skippedMarketData` (from apply_market_defaults) by walking
  // messageHistory + currentMessage in reverse — most-recent matching tool
  // result wins. Both are optional; absent data → undefined props →
  // components render nothing.
  const latestToolPayloads = useMemo(() => {
    let nextSuggestion:
      | { name: string; question: string; displayName?: string }
      | undefined;
    let skippedMarketData:
      | Array<{ marketField: string; reason: string }>
      | undefined;
    let fetchedVsApplied:
      | { fetched: number; applied: number; noTarget: number }
      | undefined;
    let appliedMarketFields:
      | Array<{ inputName: string; marketField: string; source: string }>
      | undefined;
    // market-default-apply-ux Phase 2 Session 2.7 — exact ADR-0008 partition
    // counts for the sidebar teaser, mirroring <MarketDefaultApplyReceipt>:
    // applied = location-excluded applied[] + appliedTableCells[]; pulled =
    // informationalSet + genuinelyUnmatchedSet. Sourced from the same
    // apply_market_defaults tool_result the receipt reads so the teaser count
    // never drifts from the receipt count.
    let marketApplyCounts: { applied: number; pulled: number } | undefined;
    // next-variable-curation Phase 5 Session 5.1 — server-computed group state.
    // Read from the latest of ANY of the 5 mutating tools (uniform injection via
    // `MUTATING_TOOLS`; most-recent-wins). Defensive: absent on pre-Phase-3
    // persisted turns → undefined → banner cleanly renders nothing (ADR 0003).
    let collectionState: CollectionState | undefined;
    const search = (messages: ChatMessage[]): void => {
      for (let i = messages.length - 1; i >= 0; i--) {
        const trs = messages[i].toolResults;
        if (!trs) continue;
        for (let j = trs.length - 1; j >= 0; j--) {
          const tr = trs[j];
          if (!nextSuggestion && tr.tool === 'set_inputs') {
            const ns = (
              tr.result as {
                nextSuggestion?: {
                  name?: unknown;
                  question?: unknown;
                  displayName?: unknown;
                } | null;
              } | null
            )?.nextSuggestion;
            if (
              ns &&
              typeof ns.name === 'string' &&
              typeof ns.question === 'string'
            ) {
              nextSuggestion = {
                name: ns.name,
                question: ns.question,
                displayName:
                  typeof ns.displayName === 'string'
                    ? ns.displayName
                    : undefined,
              };
            }
          }
          if (!skippedMarketData && tr.tool === 'apply_market_defaults') {
            const sk = (tr.result as { skipped?: unknown } | null)?.skipped;
            if (Array.isArray(sk)) {
              const cleaned = sk.flatMap((entry) => {
                if (
                  entry &&
                  typeof entry === 'object' &&
                  'marketField' in entry &&
                  'reason' in entry &&
                  typeof (entry as { marketField: unknown }).marketField ===
                    'string' &&
                  typeof (entry as { reason: unknown }).reason === 'string'
                ) {
                  return [
                    {
                      marketField: (entry as { marketField: string })
                        .marketField,
                      reason: (entry as { reason: string }).reason,
                    },
                  ];
                }
                return [];
              });
              skippedMarketData = cleaned.length > 0 ? cleaned : undefined;
            }
          }
          if (!fetchedVsApplied && tr.tool === 'apply_market_defaults') {
            const fva = (tr.result as { fetchedVsApplied?: unknown } | null)
              ?.fetchedVsApplied;
            if (
              fva &&
              typeof fva === 'object' &&
              typeof (fva as { fetched?: unknown }).fetched === 'number' &&
              typeof (fva as { applied?: unknown }).applied === 'number' &&
              typeof (fva as { noTarget?: unknown }).noTarget === 'number'
            ) {
              fetchedVsApplied = {
                fetched: (fva as { fetched: number }).fetched,
                applied: (fva as { applied: number }).applied,
                noTarget: (fva as { noTarget: number }).noTarget,
              };
            }
          }
          if (!appliedMarketFields && tr.tool === 'apply_market_defaults') {
            const result = tr.result as {
              applied?: unknown;
              appliedTableCells?: unknown;
            } | null;
            const scalarApplied = result?.applied;
            const tableApplied = result?.appliedTableCells;
            const items: Array<{
              inputName: string;
              marketField: string;
              source: string;
            }> = [];
            if (Array.isArray(scalarApplied)) {
              for (const entry of scalarApplied) {
                if (
                  entry &&
                  typeof entry === 'object' &&
                  typeof (entry as { inputName?: unknown }).inputName ===
                    'string' &&
                  typeof (entry as { marketField?: unknown }).marketField ===
                    'string' &&
                  typeof (entry as { source?: unknown }).source === 'string'
                ) {
                  items.push({
                    inputName: (entry as { inputName: string }).inputName,
                    marketField: (entry as { marketField: string }).marketField,
                    source: (entry as { source: string }).source,
                  });
                }
              }
            }
            if (Array.isArray(tableApplied)) {
              for (const entry of tableApplied) {
                if (
                  entry &&
                  typeof entry === 'object' &&
                  typeof (entry as { marketField?: unknown }).marketField ===
                    'string' &&
                  typeof (entry as { source?: unknown }).source === 'string'
                ) {
                  // Use namedRange if present, else construct a label from
                  // rowLabel + column for readability.
                  const namedRange = (entry as { namedRange?: unknown })
                    .namedRange;
                  const rowLabel = (entry as { rowLabel?: unknown }).rowLabel;
                  const column = (entry as { column?: unknown }).column;
                  const inputName =
                    typeof namedRange === 'string'
                      ? namedRange
                      : typeof rowLabel === 'string' &&
                        typeof column === 'string'
                      ? `${rowLabel} ${column}`
                      : (entry as { marketField: string }).marketField;
                  items.push({
                    inputName,
                    marketField: (entry as { marketField: string }).marketField,
                    source: (entry as { source: string }).source,
                  });
                }
              }
            }
            appliedMarketFields = items.length > 0 ? items : [];
          }
          if (!marketApplyCounts && tr.tool === 'apply_market_defaults') {
            const result = tr.result as {
              applied?: unknown;
              appliedTableCells?: unknown;
              informationalSet?: unknown;
              genuinelyUnmatchedSet?: unknown;
            } | null;
            // applied = location-excluded scalar applied[] + ALL appliedTableCells[]
            // (table cells are never Location.* scalars). Name-based exclusion
            // mirrors <MarketDefaultApplyReceipt>'s isNotLocationA primary rule.
            const scalar = Array.isArray(result?.applied)
              ? result!.applied
              : [];
            const appliedScalar = scalar.filter(
              (e) =>
                !(
                  e &&
                  typeof e === 'object' &&
                  typeof (e as { inputName?: unknown }).inputName ===
                    'string' &&
                  (e as { inputName: string }).inputName.includes('Location.')
                )
            ).length;
            const appliedTable = Array.isArray(result?.appliedTableCells)
              ? result!.appliedTableCells.length
              : 0;
            const info = Array.isArray(result?.informationalSet)
              ? result!.informationalSet.length
              : 0;
            const unmatched = Array.isArray(result?.genuinelyUnmatchedSet)
              ? result!.genuinelyUnmatchedSet.length
              : 0;
            marketApplyCounts = {
              applied: appliedScalar + appliedTable,
              pulled: info + unmatched,
            };
          }
          // collectionState arm — guards on the 5-tool MUTATING_TOOLS set.
          // Uniform: server injects result.collectionState on all 5 tools
          // (conversations.service.v2.ts:4491-4511). No per-tool shape logic.
          if (!collectionState && MUTATING_TOOLS.has(tr.tool)) {
            const cs = (
              tr.result as { collectionState?: CollectionState } | null
            )?.collectionState;
            if (cs) collectionState = cs; // most-recent-wins; defensive
          }
          if (nextSuggestion && skippedMarketData && collectionState) return;
        }
        if (nextSuggestion && skippedMarketData && collectionState) return;
      }
    };
    search(allMessages);
    return {
      nextSuggestion,
      skippedMarketData,
      fetchedVsApplied,
      appliedMarketFields: appliedMarketFields ?? [],
      marketApplyCounts,
      collectionState,
    };
  }, [allMessages]);

  // Create conversation via modal
  const handleCreate = useCallback(
    (title: string, selectedModelId: string) => {
      createConversation.reset();
      // Streaming overlay reset — canonical metrics will derive from
      // `/views.cellValues` of the new conversation once it loads.
      setInFlightMetrics({});
      setDefaultsFilled(false);
      // setModelGenerated REMOVED 2026-05-20 (generate_excel sunset)
      createConversation.mutate(
        { title, modelId: selectedModelId },
        {
          onSuccess: (res) => {
            const newId = res.data.id;
            setActiveConversationId(newId);
            // Keep the URL in sync with the freshly-created conversation —
            // mirrors handleSelect (model-view-ux-polish Phase 2 Session 2.1).
            // Without this the new conversation is active in state but the URL
            // stays `/builder?tab=workbench`, so a refresh / bookmark / share
            // link silently loses it. Replace (not push) — creation shouldn't
            // accumulate a back-button entry.
            navigate({
              to: '/builder',
              search: { tab, conversation: newId },
              replace: true,
            });
            setMessageHistory([]);
            setShowNewModal(false);
            if (pendingMessageRef.current) {
              const msg = pendingMessageRef.current;
              pendingMessageRef.current = null;
              setTimeout(() => {
                setMessageHistory((prev) => [
                  ...prev,
                  {
                    id: `human-${Date.now()}`,
                    role: 'human',
                    content: msg,
                    isStreaming: false,
                  },
                ]);
                sendMessage(newId, msg);
              }, 100);
            }
          },
          onError: (err) => {
            console.error('Failed to create conversation:', err);
            setUiError(
              err instanceof Error
                ? err.message
                : 'Failed to create conversation'
            );
            pendingMessageRef.current = null;
          },
        }
      );
    },
    [createConversation, sendMessage, navigate, tab]
  );

  // Send message — requires an active conversation
  const handleSend = useCallback(
    (message: string) => {
      setUiError(null);
      const humanMsg: ChatMessage = {
        id: `human-${Date.now()}`,
        role: 'human',
        content: message,
        isStreaming: false,
      };

      if (activeConversationId) {
        setMessageHistory((prev) => [...prev, humanMsg]);
        sendMessage(activeConversationId, message);
      } else {
        // No active conversation — open the modal
        setShowNewModal(true);
      }
    },
    [activeConversationId, sendMessage]
  );

  // Phase 3 Session 4 (restructured 2026-05-07) — right-panel
  // ActionButtonCluster handlers. Run Scenario fires a synthetic prompt
  // that asks the agent for a Downside/Base/Upside compact_3axis result;
  // ScenarioSummaryCard (Session 4) renders inline in chat.
  const handleRunScenario = useCallback(() => {
    handleSend(
      'Run a downside / base / upside scenario summary on the current model.'
    );
  }, [handleSend]);

  // ADR 0028 — auto-analysis turn after destructive restore.
  // Listens for `arcanum:restore-analyze` dispatched by <RestoreVersionConfirm>
  // immediately after a successful restore. Fires exactly one synthesized
  // analysis turn via handleSend (the same path as handleRunScenario). Guards:
  // - SSR guard (typeof window)
  // - conversation-id-match guard (event.detail.conversationId === activeConversationId)
  // - no-active-stream guard (streamingStatus !== 'streaming') to prevent
  //   double-fire if the event fires while a stream is already running
  // - cleanup removes listener (strict-mode-safe pattern)
  // Refs keep activeConversationId + streamingStatus fresh inside the handler
  // without re-registering the effect on every state change.
  const activeConversationIdRef = useRef(activeConversationId);
  activeConversationIdRef.current = activeConversationId;
  const streamingStatusRef = useRef(streamingStatus);
  streamingStatusRef.current = streamingStatus;

  useEffect(() => {
    if (typeof window === 'undefined') return;

    function handleRestoreAnalyze(e: Event) {
      if (!(e instanceof CustomEvent)) return;
      const detail = e.detail as
        | { conversationId: string | null; label: string }
        | undefined;
      if (!detail?.conversationId) return;
      // Conversation-id-match guard.
      if (detail.conversationId !== activeConversationIdRef.current) return;

      // 2026-05-25 (SSOT refactor): clear streaming overlay only. Canonical
      // metrics auto-re-derive from `/views.cellValues` once the restore
      // mutation's `onSuccess` invalidates `conversations.all`. No localStorage
      // cleanup needed — that cache was deleted in the SSOT refactor.
      setInFlightMetrics({});

      // No-active-stream guard — ignore if already streaming to prevent
      // duplicate turns (e.g. strict-mode double-mount firing both effects).
      if (streamingStatusRef.current === 'streaming') return;
      handleSend(
        `Restored this deal to version ${detail.label}. Re-analyze the current model state and give an updated assessment with specific recommendations.`
      );
    }

    window.addEventListener('arcanum:restore-analyze', handleRestoreAnalyze);
    return () =>
      window.removeEventListener(
        'arcanum:restore-analyze',
        handleRestoreAnalyze
      );
    // handleSend is stable (useCallback with stable deps); including it avoids
    // the exhaustive-deps lint warning. Re-registration on handleSend change
    // is correct (the handler should always use the latest version).
  }, [handleSend]);

  // table-interaction-surface (2026-06-09): `arcanum:edit-table` window-event
  // bus → composer mode-swap. Fires when the user clicks "Edit" on a
  // <TableEditReceipt> card in the transcript. The handler:
  //   1. Looks up the `tableDef` by event `table` key from the model schema.
  //   2. Builds a `table-edit` InteractionRequest (schema-native props).
  //   3. Flips `composerMode` to `'interaction'` so the <InteractionSurface>
  //      replaces the chat composer zone.
  // Guards:
  //   - SSR guard (typeof window)
  //   - conversationId-match guard (event.detail.conversationId must match the
  //     currently active conversation — prevents stale events from a prior nav)
  //   - adminReadOnly guard (suppressed — no mutation affordances in read-only)
  // Refs keep the reactive state fresh inside the handler without re-registering
  // on every change (mirrors `arcanum:restore-analyze` pattern above).
  const tableInputsRef = useRef(tableInputs);
  tableInputsRef.current = tableInputs;
  const activeConversationIdForInteractionRef = useRef(activeConversationId);
  activeConversationIdForInteractionRef.current = activeConversationId;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (adminReadOnly) return;

    function handleEditTable(e: Event) {
      if (!(e instanceof CustomEvent)) return;
      const detail = e.detail as EditTableEventDetail | undefined;
      if (!detail?.table || !detail.conversationId) return;
      // Conversation-id-match guard.
      if (
        detail.conversationId !== activeConversationIdForInteractionRef.current
      )
        return;

      // Look up the tableDef from the model schema by the table key.
      const tableDef = tableInputsRef.current.find(
        (t) => t.name === detail.table
      );
      if (!tableDef) return; // unknown table key — no-op

      const request: InteractionRequest = {
        kind: 'table-edit',
        id: `edit-table-${detail.table}-${Date.now()}`,
        prompt: `Edit ${tableDef.displayName}`,
        tableDef,
        conversationId: detail.conversationId,
      };
      setActiveInteraction(request);
      setComposerMode('interaction');
    }

    window.addEventListener(EDIT_TABLE_EVENT, handleEditTable);
    return () => window.removeEventListener(EDIT_TABLE_EVENT, handleEditTable);
    // adminReadOnly is stable per route render; including it avoids stale closure.
  }, [adminReadOnly]);

  // Dismiss handler: restores chat composer + clears active interaction.
  // market-default-apply-ux Phase 2 Session 2.4: also clears activeEligibleInputs
  // so Escape / backdrop-click paths return to a clean default.
  const handleInteractionDismiss = useCallback(() => {
    setComposerMode('chat');
    setActiveInteraction(null);
    setActiveEligibleInputs([]);
  }, []);

  // market-default-apply-ux Phase 2 Session 2.4 — path-B wiring.
  // Derives BOTH the dropdown-display `options[]` ({id,label,hint?}) AND the
  // full hook-ready `eligibleInputs[]` ({name,displayName,dataType,description?})
  // from the model's `inputDefinitions`, filtered to non-location inputs (R8).
  // Derive DIRECTLY from inputDefinitions — NOT from VariablesPanel._scalarInputOptions
  // (lossy {name,displayName} only, drops dataType/description — B2/I1).
  function buildEligibleInputs() {
    const defs = (modelDetail?.data?.inputs ?? []).filter(
      (d) => d.dataType !== 'location'
    );
    const options = defs.map((d) => ({
      id: d.name,
      label: d.displayName,
      hint: d.description || undefined,
    }));
    const eligibleInputs = defs.map((d) => ({
      name: d.name,
      displayName: d.displayName,
      dataType: d.dataType,
      description: d.description || undefined,
    }));
    return { options, eligibleInputs };
  }

  // market-default-apply-ux Phase 2 Session 2.4 — path-B handler.
  // Constructs a `market_default_apply` InteractionRequest client-side (mirror
  // `handleEditTable`'s `const request: InteractionRequest = { kind:'table-edit', ... }`
  // precedent — path B, ADR 0001; no agent synthetic tool).
  // `aliasMatchState` carries the entry-point: informational rows pass
  // 'intentionally_unmapped'; couldn't-auto-match rows pass 'genuinely_unmatched'.
  const handleApplyMarketDefaultToInput = useCallback(
    (field: MarketFieldValueUnit, aliasState: AliasMatchState) => {
      if (adminReadOnly) return; // mirror handleEditTable's guard
      const { options, eligibleInputs } = buildEligibleInputs();
      // marketDefaultApplyInteractionSchema requires options.min(1); if the
      // model has zero eligible (non-location) inputs there is nothing to apply
      // to — do not open an arm with an empty dropdown (gap-analysis MINOR-4).
      if (options.length === 0) return;
      const request: MarketDefaultApplyInteraction = {
        kind: 'market_default_apply',
        id: `market-default-apply-${field.marketField}-${Date.now()}`,
        prompt: `Apply ${field.marketField.replace(
          /_/g,
          ' '
        )} to a model input`,
        conversationId: activeConversationId!,
        marketField: field.marketField,
        value: field.value,
        unit: field.unit,
        aliasMatchState: aliasState,
        suggestions: [],
        options,
        explainPrompt: 'Why this input? (optional)',
      };
      setActiveInteraction(request);
      setActiveEligibleInputs(eligibleInputs);
      setComposerMode('interaction');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [adminReadOnly, activeConversationId, modelDetail]
  );

  // Provenance Report download — fetches GET /api/conversations/:id/provenance
  // (returns the audit-trail JSON) and triggers a client-side blob download.
  // Errors surface via setUiError; no toast / modal.
  const handleDownloadProvenance = useCallback(async () => {
    if (!activeConversationId) return;
    try {
      const json = await api.get<unknown>(
        `/api/conversations/${activeConversationId}/provenance`
      );
      const blob = new Blob([JSON.stringify(json, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `provenance-${activeConversationId}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setUiError(
        err instanceof Error
          ? `Provenance download failed: ${err.message}`
          : 'Provenance download failed'
      );
    }
  }, [activeConversationId]);

  const handleFork = useCallback(() => {
    if (!activeConversationId || !latestVersion) return;
    forkConversation.mutate(
      { conversationId: activeConversationId, targetVersion: latestVersion },
      {
        onSuccess: (res) => {
          setShowForkConfirm(false);
          const newId = res.data.id;
          setActiveConversationId(newId);
          setMessageHistory([]);
          setInFlightMetrics({});
          setDefaultsFilled(false);
          // setModelGenerated/setExcelGenerating REMOVED 2026-05-20 (generate_excel sunset)
        },
        onError: (err) => {
          setShowForkConfirm(false);
          setUiError(
            err instanceof Error ? err.message : 'Failed to fork conversation'
          );
        },
      }
    );
  }, [activeConversationId, latestVersion, forkConversation]);

  // In-place upgrade to the latest model version (keeps the conversation +
  // chat history). The confirm lives inside <ModelUpgradeBanner>.
  const handleUpgrade = useCallback(() => {
    if (!activeConversationId) return;
    upgradeModelVersion.mutate(activeConversationId, {
      onError: (err) => {
        setUiError(
          err instanceof Error ? err.message : 'Failed to update model version'
        );
      },
    });
  }, [activeConversationId, upgradeModelVersion]);

  const isStreaming =
    streamingStatus === 'connecting' || streamingStatus === 'streaming';

  // Billing-driven read-only gate. Active when usage_limiting_enabled=true AND
  // pct >= 90% for non-admin users. Disables ChatInput, New Deal button, and
  // ModelView edit calls (the Model tab's <ModelLayout readOnly> prop).
  const billingReadOnly = useBillingReadOnly();

  // ADR 0027 — single useModelEdit instance for the Variables-panel affordance
  // path (LocationInputModal). The grid path uses its own instance inside
  // <InputBranch> / CellDetailPanel; ADR 0004 allows multiple hook instances
  // (one per call site) as long as they all hit the same POST /model-edit
  // endpoint — the "one resolver, one owner-write hook" invariant lives on the
  // server side.
  const locationModalMutation = useModelEdit();

  const showDefaultsButton =
    activeConversationId &&
    collected >= 2 &&
    pending > 0 &&
    !isStreaming &&
    !defaultsFilled;
  // 2026-05-20: removed `!modelGenerated && !modelAlreadyGenerated` gates;
  // generate_excel sunset means there's no terminal "model generated" state
  // and users can always continue iterating + filling defaults.

  // EMPTY REGISTRY: zero models in `model_registry`. No conversation can be
  // created until an admin uploads one. First-class render path — sits between
  // loading and the normal builder layout. The "+ New Analysis" button in the
  // sidebar is gated below regardless, but this whole-page panel is the
  // primary surface so analysts know who to talk to.
  const isModelRegistryEmpty = !modelsLoading && modelsList.length === 0;

  if (isModelRegistryEmpty) {
    return (
      <div
        className="flex h-full items-center justify-center p-12"
        aria-live="polite"
      >
        <EmptyState
          icon={<Wrench size={32} className="text-text-muted" />}
          title="Platform not yet configured"
          description="No financial models are currently available. Contact your administrator to enable the Builder."
          action={
            isAdmin ? (
              <span className="text-xs font-mono text-text-dim">
                Admins: upload a model at{' '}
                <Link
                  to="/admin"
                  search={{ tab: 'models' }}
                  className="text-accent hover:text-text-bright underline underline-offset-2"
                >
                  /admin?tab=models
                </Link>
              </span>
            ) : (
              <span className="text-xs text-text-dim">
                Contact your administrator to upload a financial model.
              </span>
            )
          }
        />
      </div>
    );
  }

  // MISSING MODEL: the active conversation is pinned to a (modelId, modelVersion)
  // that no longer resolves. Source can be either the conversation GET (returns
  // 410 → MissingModelError) or a mid-conversation SSE error from the streaming
  // hook. Renders the recovery panel above (and replacing) the chat thread.
  const missingFromConversation =
    conversationError instanceof MissingModelError
      ? conversationError.missing
      : null;
  const missing = missingFromConversation ?? streamingMissing;

  // CONVERSATION-404 FRIENDLY RECOVERY: the conversation in the URL doesn't
  // exist (deleted, never existed, or owned by a different user — RLS returns
  // 404 for all three to avoid leaking existence). Render an EmptyState in
  // the same sidebar shell as the missing-model branch so the user can pick
  // a different conversation. The MissingModelError exclusion is defensive —
  // that subclass is HTTP 410 not 404 so it can't false-positive, but the
  // explicit narrow makes the intent obvious.
  // (model-view-ux-polish Phase 2 Session 2.1, 2026-04-27)
  const conversationNotFound =
    conversationError instanceof ApiError &&
    !(conversationError instanceof MissingModelError) &&
    conversationError.status === 404;

  if (missing && activeConversationId) {
    return (
      <div className="flex h-full overflow-hidden">
        <ConversationSidebar
          conversations={conversations}
          activeId={activeConversationId}
          onSelect={handleSelect}
          onCreate={() => setShowNewModal(true)}
          createDisabled={billingReadOnly}
          createDisabledReason="Monthly allowance reached — new deals paused"
        />
        <div className="flex-1 flex flex-col overflow-hidden min-w-0 p-6">
          <MissingModelPanel
            conversationId={activeConversationId}
            conversationTitle={
              detail?.title ?? activeConversation?.title ?? activeConversationId
            }
            missing={missing}
            onForked={(newId, orphanedInputCount) => {
              setActiveConversationId(newId);
              setMessageHistory([]);
              setInFlightMetrics({});
              setDefaultsFilled(false);
              // setModelGenerated/setExcelGenerating REMOVED 2026-05-20 (generate_excel sunset)
              setForkBanner({ orphanedInputCount });
            }}
            onDeleted={() => {
              setActiveConversationId(null);
              setMessageHistory([]);
              setInFlightMetrics({});
            }}
          />
        </div>
      </div>
    );
  }

  if (conversationNotFound && activeConversationId) {
    return (
      <div className="flex h-full overflow-hidden">
        <ConversationSidebar
          conversations={conversations}
          activeId={null}
          onSelect={handleSelect}
          onCreate={() => setShowNewModal(true)}
          createDisabled={billingReadOnly}
          createDisabledReason="Monthly allowance reached — new deals paused"
        />
        <div
          className="flex-1 flex items-center justify-center min-w-0 p-6"
          data-testid="empty-state-404"
        >
          <EmptyState
            title="CONVERSATION NOT FOUND"
            description="This conversation was deleted or never existed. Start a new analysis or pick one from your pipeline."
            action={
              <Link
                to="/builder"
                search={{ tab: 'workbench' }}
                className="text-sm font-mono text-accent hover:text-text-bright transition-colors"
                data-testid="cta-back-to-builder"
              >
                ← Back to Builder
              </Link>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden">
      {/* SendDealLinkModal — mounted once at Builder root so it survives tab
          flips. Self-gates on the `arcanum:send-deal-link` window event +
          `conversationId` match. (external-sharing Phase 3 Session 3.2,
          2026-05-09.)
          admin-deal-oversight ADR 0007: suppressed in adminReadOnly mode — no
          sharing affordance for admin observers. */}
      {!adminReadOnly && (
        <SendDealLinkModal conversationId={activeConversationId} />
      )}
      {/* ShareBackModal — sibling of SendDealLinkModal. Self-gates on the
          `arcanum:share-back` window event from <ShareForkBanner>'s
          [Share back] button + `forkConversationId` match. Inert when the
          active conversation isn't a fork (forkParentShareId === null).
          (external-sharing Phase 4 Session 4.4, 2026-05-09.)
          admin-deal-oversight ADR 0007: suppressed in adminReadOnly mode. */}
      {!adminReadOnly && (
        <ShareBackModal
          conversationId={activeConversationId}
          forkParentShareId={detail?.forkedFromShareId ?? null}
        />
      )}
      {/* BrowseVersionsModal — sibling of SendDealLinkModal/ShareBackModal.
          Survives tab flips + sidebar hides. Self-gates on the
          `arcanum:open-versions` window event + conversation-id-match guard.
          ADR 0028: versionReady + blockingReason threaded for the destructive-
          restore "save current state first" escape hatch inside
          <RestoreVersionConfirm>.
          (conversation-model-versioning Phase 5 Session 3, 2026-05-17.) */}
      <BrowseVersionsModal
        conversationId={activeConversationId}
        currentVersionId={activeConversation?.currentVersionId}
        versionReady={versionReadiness.ready}
        blockingReason={restoreBlockingReason}
      />
      {/* builder-ux-overhaul Phase 4 Session 3 — second <RestoreVersionConfirm>
          mount driven by the drifted-pill "Restore to active" action. Pre-targeted
          to `currentVersionId` from `useActiveVersionDrift` (NOT accepted / display
          version — see blueprint Session 3 "Restore wiring DECISION" option (a) +
          DIV-4 version-pointer quad).
          The destructive save-first escape hatch + 409 RUN_IN_PROGRESS path are
          INHERITED from <RestoreVersionConfirm> itself — not re-implemented here.
          `versionReady` + `blockingReason` are the same values passed to
          <BrowseVersionsModal> above (hoisted const `restoreBlockingReason`).
          On success the hook's `onSuccess` invalidates `conversations.all` →
          the drift hook (nested under `conversations.detail(id)`) recomputes to
          in-sync automatically, closing the pill's drifted state. */}
      <RestoreVersionConfirm
        open={restoreToActiveOpen}
        onClose={() => setRestoreToActiveOpen(false)}
        onRestoreSuccess={() => setRestoreToActiveOpen(false)}
        conversationId={activeConversationId}
        versionId={activeVersionDrift.data?.currentVersionId ?? null}
        versionLabel={activeVersionDrift.data?.currentVersionLabel ?? ''}
        versionReady={versionReadiness.ready}
        blockingReason={restoreBlockingReason}
      />
      {/* LocationInputModal — root-mounted window-event host for the
          VariablesPanel address picker (conversation-model-versioning Phase 6
          Session 1+4, 2026-05-18). ADR 0027: onResolved fires useModelEdit
          carrying namedRange (inputKey) + placeId + sessionToken so the
          server's postModelEdit location hook enriches the agent_state via
          resolvePropertyLocation. Single owner-write hook (ADR 0004).
          admin-deal-oversight ADR 0007: suppressed in adminReadOnly — address
          picker is a mutation affordance; server would 403 anyway. */}
      {!adminReadOnly && (
        <LocationInputModal
          conversationId={activeConversationId}
          onResolved={(inputKey, v) => {
            if (!activeConversationId) return;
            locationModalMutation.mutate({
              conversationId: activeConversationId,
              namedRange: inputKey,
              value: v.formattedAddress,
              placeId: v.placeId,
              sessionToken:
                v.sessionToken !== undefined
                  ? JSON.stringify(v.sessionToken)
                  : undefined,
            });
          }}
        />
      )}
      {/* Sidebar hidden in Model-tab fullscreen per spec §6 — Phase 3 Session 3.3.
          Additionally hidden on Signal + Model tabs (deal-signal-overhaul Phase 5
          redesign 2026-05-12) — both surfaces claim full container width for their
          data-dense layouts; deals are switched from Workbench (the only tab
          retaining the conversation sidebar). BuilderTabBar + BuilderToolbar
          remain visible above the body.
          admin-deal-oversight ADR 0007: adminReadOnly suppresses the sidebar
          entirely — the admin arrived via the All Deals table and uses the
          "← Back to deals" button; a deal-list sidebar for foreign conversations
          would be misleading. */}
      {!adminReadOnly &&
        !isModelFullscreen &&
        tab !== 'signal' &&
        tab !== 'model' && (
          <ConversationSidebar
            conversations={conversations}
            activeId={activeConversationId}
            onSelect={handleSelect}
            onCreate={() => setShowNewModal(true)}
            createDisabled={billingReadOnly}
            createDisabledReason="Monthly allowance reached — new deals paused"
          />
        )}

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* admin-deal-oversight ADR 0007: AdminViewingBanner mounted at the TOP
            of the content area (above the tab body, inside the normal scroll
            layout — no new layout wrapper). Visible on ALL tabs so the admin
            always knows they're in read-only mode. The "← Back to deals" link
            lives INSIDE the banner (single affordance across all admin views). */}
        {adminReadOnly && (
          <div className="px-5 pt-3 pb-1 shrink-0">
            <AdminViewingBanner
              dealTitle={
                detail?.title ?? activeConversation?.title ?? 'Untitled deal'
              }
              ownerName="an Arcanum user"
            />
          </div>
        )}
        {/*
         * external-sharing Phase 4 Session 4.3 (2026-05-09) — fork lineage
         * banner. Renders ABOVE BuilderTabBar when `forkedFromShareId` is
         * non-null. Self-gates internally (returns null otherwise) so the
         * mount site stays unconditional and the call site is a single
         * line. Padding matches the surrounding layout (px-5).
         */}
        {activeConversationId &&
          detail?.forkedFromShareId &&
          !isModelFullscreen && (
            <div className="px-5 pt-3 shrink-0">
              <ShareForkBanner
                conversationId={activeConversationId}
                forkedFromShareId={detail.forkedFromShareId}
                forkedFromConversationId={
                  detail.forkedFromConversationId ?? null
                }
              />
            </div>
          )}
        {/* Model-tab fullscreen (2026-05-25): the Maximize2 button at
            ModelLayout.tsx:701 toggles `isModelFullscreen`. When true on
            the Model tab, hide the BuilderTabBar + ModelHeaderStrip + any
            transient banners so the model claims the full builder area;
            outer page header + LeftNav rail stay (user choice 2026-05-25:
            "Builder-area only"). The collapse affordance + Esc shortcut
            live in ModelLayout — see Phase 3 Session 3.3. */}
        {!(tab === 'model' && isModelFullscreen) && (
          <BuilderTabBar
            activeTab={tab}
            modelDisabled={!modelId}
            // Phase 6 Session 3 (2026-05-08) — Signal tab is gated on the
            // model having an admin-assigned `signal_variant`. The schema
            // value lives on the registry row + flows through `useModel` →
            // `modelDetail.data.signal_variant` (Session 1's getSchema merge).
            // Disabled (with tooltip) when null/undefined; users see the
            // dimmed tab + admin guidance until the variant is assigned.
            signalDisabled={!modelDetail?.data?.signal_variant}
            rightSlot={
              activeConversationId && !adminReadOnly ? (
                <>
                  {/* conversation-model-versioning Phase 5 — single
                    consolidated version entry point on the tab row,
                    opposite the tab strip. Replaces the three
                    VariablesPanel-header controls (Versions / Create
                    version / Accept). Present on ALL tabs.
                    admin-deal-oversight ADR 0007: suppressed in adminReadOnly. */}
                  <ManageVersionsButton
                    conversationId={activeConversationId}
                    schemaInputs={schemaInputDefs}
                    inputProvenance={inputProvenance}
                    orphanedInputs={routeOrphanedInputs}
                    dealMetadata={routeDealMetadata}
                  />
                  {/* SendDealLinkButton — admin-deal-oversight ADR 0007:
                      suppressed in adminReadOnly (no sharing from admin view). */}
                  <SendDealLinkButton
                    conversationId={activeConversationId}
                    hasVersion={hasVersion}
                    inSync={inSync}
                  />
                </>
              ) : undefined
            }
          />
        )}
        {/* builder-ux-overhaul Phase 3 Session 2 (2026-05-22):
            <DealHeaderStrip> mounts on all 3 tabs.
            - Workbench: showHeroReturns={false}; rendered here above the
              surviving BuilderToolbar widgets.
            - Signal: strip is rendered inside <SignalOperatingAsset> /
              <SignalNewDevelopment> (those components own their subline).
            - Model: strip is rendered inside <ModelHeaderStrip>.
            Signal-variant chip sourced from modelDetail.data.signal_variant
            (verbatim research/phase-3.md § 3 topic 3). */}
        {tab === 'workbench' && (
          <DealHeaderStrip
            conversationTitle={
              activeConversationId
                ? detail?.title ?? activeConversation?.title
                : undefined
            }
            signalVariant={
              activeConversationId
                ? modelDetail?.data?.signal_variant ?? null
                : null
            }
            subline={
              activeConversationId
                ? (modelName ?? activeModelSummary?.name ?? '').toUpperCase()
                : ''
            }
            showHeroReturns={false}
            pillSlot={
              <ActiveVersionPill
                conversationId={versionConversationId}
                onRestoreClick={() => setRestoreToActiveOpen(true)}
                {...pillCreateProps}
              />
            }
          />
        )}
        {/* `<BuilderToolbar>` is Workbench-only as of `deal-signal-overhaul`
            Phase 5 redesign 2026-05-12. Schema-version title block RETIRED in
            builder-ux-overhaul Phase 3 Session 2 (2026-05-22) — model-name,
            v{version} badge, and deal-name span moved to <DealHeaderStrip>.
            Surviving widgets: compact-progress-bar + <NextFieldGuide> +
            <RealtimeStatusBadge> (rehomed per spec WS1 §1). */}
        {tab === 'workbench' && (
          <BuilderToolbar
            collected={collected}
            pending={pending}
            realtimeStatus={activeConversationId ? realtimeStatus : undefined}
            syncStatus={activeConversationId ? syncStatus : undefined}
            collectionState={
              latestToolPayloads.collectionState ??
              serverCollectionState?.collectionState ??
              null
            }
          />
        )}
        {forkBanner && (
          <ForkSuccessBanner
            orphanedInputCount={forkBanner.orphanedInputCount}
            onDismiss={() => setForkBanner(null)}
          />
        )}
        {/* ADR 0007: model-version upgrade mutates the conversation — gated in adminReadOnly. */}
        {!adminReadOnly &&
          showForkBanner &&
          conversationVersion &&
          latestVersion && (
            <ModelUpgradeBanner
              currentVersion={conversationVersion}
              latestVersion={latestVersion}
              onUpgrade={handleUpgrade}
              upgrading={upgradeModelVersion.isPending}
            />
          )}
        {(uiError || streamingError) && (
          <div className="px-5 py-2.5 bg-danger/[0.06] border-b border-danger/13 flex items-center justify-between shrink-0">
            <span className="text-sm font-mono text-danger">
              {uiError || streamingError}
            </span>
            <button
              onClick={() => {
                // F09 (2026-05-22): clear BOTH the route-local uiError AND the
                // streaming hook's error — the banner shows on either, so
                // clearing only uiError left a streamingError banner stuck.
                setUiError(null);
                clearStreamingError();
              }}
              className="text-xs font-mono text-danger/60 hover:text-danger cursor-pointer bg-transparent border-none"
            >
              Dismiss
            </button>
          </div>
        )}
        {/* Phase 7 overload retry (2026-05-15) — retriable error card with a
            single-click Retry button. Distinct visual from the generic danger
            banner above (warn-tinted, not danger-tinted) because the failure
            is transient and the user's message hasn't been billed. Replaces
            the raw JSON envelope that previously dumped into chat content. */}
        {retriableError && (
          <div className="px-5 py-3 bg-warn/[0.06] border-b border-warn/13 flex items-center justify-between gap-4 shrink-0">
            <div className="flex flex-col gap-0.5 min-w-0">
              <span className="text-sm font-body text-warn">
                Anthropic is briefly overloaded
              </span>
              <span className="text-xs font-body text-text-muted">
                This is usually transient — your message hasn't been charged.
                Please retry.
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => retryStreaming()}
                className="px-3 py-1.5 text-xs font-mono uppercase tracking-wider text-warn border border-warn/40 hover:bg-warn/10 cursor-pointer bg-transparent rounded-[2px]"
              >
                Retry
              </button>
            </div>
          </div>
        )}
        {/* Phase 7 (2026-05-15) — inline "Retrying… (N/total)" indicator. Sits
            above the chat input. Subtle (text-warn at low opacity) so it
            doesn't alarm — sets expectation that the 1-7s wait is intentional. */}
        {retryStatus && (
          <div className="px-5 py-1.5 bg-warn/[0.04] border-b border-warn/10 shrink-0">
            <span className="text-xs font-mono text-warn/80">
              Service is busy — retrying… (attempt {retryStatus.attempt}/
              {retryStatus.totalAttempts})
            </span>
          </div>
        )}
        {/* Phase 6 Session 3 (2026-05-08) — 5-hero header strip mounts above
            the body on Signal + Model tabs (NOT Workbench). Pulls live KPI
            values from the conversation's metricHistory + outputs. Session 4
            replaces the MVP composition (`<MetricsCard>`-based) with a
            dedicated `<HeroMetricCell>` primitive set. */}
        {(tab === 'signal' || tab === 'model') && activeConversationId && (
          <>
            {/* `signal-model-cta-strip` retired by `deal-signal-overhaul`
                Phase 5 redesign 2026-05-12. Previously hosted a deal-switcher
                dropdown (left, surfacing the same deal title shown as the
                Signal-body h1 below — duplicate) + a `<SendDealLinkButton>`
                (right). The button is now part of `<BuilderTabBar>`'s
                `rightSlot` so it shares the row with the tab strip (matches
                Lovable header layout). Switching deals continues to work
                via the sidebar; the dropdown was redundant with the active-
                row highlight in `<ConversationSidebar>`. */}
            {/* deal-signal-overhaul Phase 4 Session 5 (2026-05-12) — the
                Signal tab now owns its own 5-cell hero strip via
                `<HeroReturns>` inside `<SignalOperatingAsset>` /
                `<SignalNewDevelopment>`. The top-of-body
                `<HeroMetricsHeader>` stays on the Model tab (no in-body
                hero strip there) but is suppressed on Signal to avoid the
                duplicate render. */}
            {tab === 'model' && !isModelFullscreen && (
              <ModelHeaderStrip
                conversationId={activeConversationId}
                modelId={modelId ?? ''}
                modelVersion={conversationVersion ?? undefined}
                signalVariant={modelDetail?.data?.signal_variant ?? null}
                title={
                  detail?.title ?? activeConversation?.title ?? 'Untitled deal'
                }
                modelName={modelName ?? undefined}
                metricHistory={agentStateRefined?.metricHistory ?? null}
                liveMetrics={liveMetrics}
                modelSchema={modelDetail?.data ?? null}
                pillSlot={
                  <ActiveVersionPill
                    conversationId={versionConversationId}
                    onRestoreClick={() => setRestoreToActiveOpen(true)}
                    {...pillCreateProps}
                  />
                }
              />
            )}
          </>
        )}
        {tab === 'workbench' ? (
          <>
            {!activeConversationId && conversations.length === 0 ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center space-y-4 max-w-md">
                  <div className="text-4xl mb-2">&#9670;</div>
                  <h2 className="text-lg font-mono font-semibold text-text-bright">
                    No conversations yet
                  </h2>
                  <p className="text-sm font-mono text-text-muted">
                    Click &quot;+ New Analysis&quot; in the sidebar to begin
                    your first deal analysis.
                  </p>
                </div>
              </div>
            ) : (
              <ChatThread
                messages={allMessages}
                initials={initials}
                securityBlock={securityBlock}
                inputProvenance={agentStateRefined?.inputProvenance ?? null}
                inputs={views?.inputs}
                modelSchema={modelDetail?.data ?? null}
                onSynthesizedTurn={handleSend}
                modelId={modelId}
                conversationId={activeConversationId}
                readOnly={billingReadOnly || adminReadOnly}
                onApplyToInput={handleApplyMarketDefaultToInput}
              />
            )}
            {/* admin-deal-oversight ADR 0007: "Fill remaining with defaults"
                button + ChatInput + StopButton suppressed in adminReadOnly —
                these are the Workbench mutation affordances. The ChatThread
                above remains visible (read-only).
                table-interaction-surface (2026-06-09): when composerMode is
                'interaction', the <InteractionSurface> REPLACES the defaults
                row + ChatInput + StopButton. Dismiss restores 'chat'. */}
            {!adminReadOnly &&
              (composerMode === 'interaction' && activeInteraction ? (
                <InteractionSurface
                  request={activeInteraction}
                  density="workbench"
                  currentInputs={views?.inputs}
                  onDismiss={handleInteractionDismiss}
                  billingReadOnly={billingReadOnly}
                  adminReadOnly={adminReadOnly}
                  eligibleInputs={activeEligibleInputs}
                />
              ) : (
                <>
                  {showDefaultsButton && (
                    <div className="px-6 py-2 border-t border-panel-border bg-panel-bg shrink-0 flex items-center gap-3">
                      <button
                        onClick={() => {
                          setDefaultsFilled(true);
                          handleSend('Accept all remaining defaults.');
                        }}
                        className="px-4 py-2 bg-accent-dim border border-accent/15 rounded-[3px] text-accent text-xs font-mono font-semibold cursor-pointer hover:bg-accent/13 transition-colors"
                      >
                        Fill remaining with defaults
                      </button>
                      <span className="text-xs font-mono text-text-dim">
                        {collected} set &middot;{' '}
                        {locationRequiredUnresolved
                          ? 'property location required'
                          : `${pending} using defaults`}
                      </span>
                    </div>
                  )}
                  <div className="flex items-stretch gap-2">
                    <div className="flex-1">
                      <ChatInput
                        onSend={handleSend}
                        loading={isStreaming || createConversation.isPending}
                        disabled={billingReadOnly}
                        placeholder={
                          billingReadOnly
                            ? 'Monthly allowance reached — new messages paused'
                            : undefined
                        }
                      />
                    </div>
                    {streamingStatus === 'streaming' && (
                      <div className="flex items-center">
                        <StopButton onAbort={abortStreaming} />
                      </div>
                    )}
                  </div>
                </>
              ))}
          </>
        ) : tab === 'signal' ? (
          // Phase 6 Sessions 4 + 5 (2026-05-08) — Signal tab body dispatches
          // on the model's `signal_variant`. Two SEPARATE variant components
          // (configuration over conditionals — no `mode` prop, no shared
          // primitives branching internally). `signal_variant === null` is
          // gated upstream by the Session 3 tab-disabled state, but we still
          // render the placeholder branch defensively so a stale
          // `?tab=signal` URL doesn't crash.
          modelDetail?.data?.signal_variant === 'operating-asset' &&
          activeConversationId ? (
            <>
              <SignalOperatingAsset
                context={{
                  kind: 'conversation',
                  conversationId: activeConversationId,
                }}
                modelId={modelDetail.data.id}
                modelVersion={modelDetail.data.version}
                aerialLatLng={views?.aerialLatLng ?? null}
                metricHistory={agentStateRefined?.metricHistory ?? null}
                pageSize={4}
                conversationTitle={
                  detail?.title ?? activeConversation?.title ?? 'Untitled deal'
                }
                pillSlot={
                  <ActiveVersionPill
                    conversationId={versionConversationId}
                    onRestoreClick={() => setRestoreToActiveOpen(true)}
                    {...pillCreateProps}
                  />
                }
              />
              <SignalAnchorListener
                section={sectionParam}
                assumption={assumptionParam}
                modelSchema={modelDetail?.data ?? null}
                onConsume={consumeSignalAnchor}
              />
            </>
          ) : modelDetail?.data?.signal_variant === 'new-development' &&
            activeConversationId ? (
            <>
              <SignalNewDevelopment
                context={{
                  kind: 'conversation',
                  conversationId: activeConversationId,
                }}
                modelId={modelDetail.data.id}
                modelVersion={modelDetail.data.version}
                aerialLatLng={views?.aerialLatLng ?? null}
                metricHistory={agentStateRefined?.metricHistory ?? null}
                pageSize={4}
                conversationTitle={
                  detail?.title ?? activeConversation?.title ?? 'Untitled deal'
                }
                pillSlot={
                  <ActiveVersionPill
                    conversationId={versionConversationId}
                    onRestoreClick={() => setRestoreToActiveOpen(true)}
                    {...pillCreateProps}
                  />
                }
              />
              <SignalAnchorListener
                section={sectionParam}
                assumption={assumptionParam}
                modelSchema={modelDetail?.data ?? null}
                onConsume={consumeSignalAnchor}
              />
            </>
          ) : (
            <div
              className="flex-1 flex items-center justify-center"
              data-testid="signal-body-placeholder"
            >
              <div className="text-center space-y-2 max-w-md font-mono text-sm">
                <div className="text-2xl text-accent">◆</div>
                <p className="text-text-bright">Signal view</p>
                <p className="text-text-muted">
                  This model doesn&apos;t have a Signal variant configured.
                </p>
              </div>
            </div>
          )
        ) : (
          <ModelLayoutContainer
            conversationId={activeConversationId}
            onCreateNew={() => setShowNewModal(true)}
            onViewInConversation={() =>
              navigate({
                to: '/builder',
                search: (prev) => ({ ...prev, tab: 'workbench' }),
              })
            }
            bridgeRef={modelViewBridgeRef}
            onFullscreenChange={setIsModelFullscreen}
            uiPrefs={detail?.uiPrefs}
            cellParam={cellParam}
            namedRangeParam={namedRangeParam}
            searchParam={searchParam}
            onConsumeDeepLink={consumeDeepLink}
            onSearchChange={handleSearchChange}
            readOnly={billingReadOnly || adminReadOnly}
          />
        )}
        {/* NarrativeBar mounted on Signal tab only (`deal-signal-overhaul`
            Phase 5 redesign 2026-05-12). Original `agent-ux-optimization`
            Phase 6 Session 7 (2026-05-08) shipped per-tab mounts on
            Workbench + Signal + Model; the redesign removed the Workbench
            mount (the `<ChatInput>` already owns the composer surface —
            the bar was duplicate chrome) and the Model mount (the 3-pane
            spreadsheet view doesn't need a chat affordance — users who
            want to ask questions switch back to Workbench, or expand the
            Signal-tab overlay if they're already on Signal).

            Signal-tab bar gains a `renderAboveComposer` slot that surfaces
            the SAME `<ChatThread>` Workbench renders, reading the same
            `allMessages` + streaming state already in scope. No second
            `useStreaming()` mount — sibling composition. Requires
            explicit user click on the chevron to expand. */}
        {/* admin-deal-oversight ADR 0007: NarrativeBar composer suppressed in
            adminReadOnly — no agent-run path for a foreign deal. */}
        {tab === 'signal' && !adminReadOnly && (
          <NarrativeBar
            mode="prompt"
            headerBadge="SIGNAL"
            dealName={detail?.title ?? activeConversation?.title}
            disabled={billingReadOnly}
            disabledPlaceholder="Monthly allowance reached — new messages paused"
            onSubmit={(content) => {
              if (isStreaming || billingReadOnly) return false;
              handleSend(content);
              return true;
            }}
            renderAboveComposer={(expanded) =>
              expanded && activeConversationId ? (
                <div className="max-h-[45vh] overflow-y-auto border-t border-panel-border bg-bg">
                  <ChatThread
                    messages={allMessages}
                    initials={initials}
                    securityBlock={securityBlock}
                    inputProvenance={agentStateRefined?.inputProvenance ?? null}
                    inputs={views?.inputs}
                    modelSchema={modelDetail?.data ?? null}
                    onSynthesizedTurn={handleSend}
                    modelId={modelId}
                    conversationId={activeConversationId}
                    readOnly={billingReadOnly || adminReadOnly}
                    onApplyToInput={handleApplyMarketDefaultToInput}
                  />
                </div>
              ) : null
            }
            interaction={
              composerMode === 'interaction' && activeInteraction
                ? activeInteraction
                : undefined
            }
            onInteractionDismiss={handleInteractionDismiss}
            currentInputs={views?.inputs}
            interactionBillingReadOnly={billingReadOnly}
            eligibleInputs={activeEligibleInputs}
          />
        )}
      </div>

      {/* Right Panel — Workbench tab only. The Signal + Model tabs use their
          own layouts (Signal: hero strip + variant body; Model: three-pane
          spreadsheet). The ModelOverviewPanel + VariablesPanel surfaces don't
          apply outside the chat workflow. */}
      {tab === 'workbench' && (
        <div className="w-72 2xl:w-80 bg-void border-l border-panel-border flex flex-col shrink-0 overflow-y-auto">
          <ModelOverviewPanel
            metrics={liveMetrics}
            outputDefinitions={outputDefinitions}
            allNamedRanges={allNamedRanges}
            displayConfig={modelDetail?.data?.displayConfig}
            userInputCount={collected}
            totalInputs={totalInputs}
            metricHistory={agentStateRefined?.metricHistory}
            onRunScenario={handleRunScenario}
            onDownloadProvenance={handleDownloadProvenance}
            // ADR 0007: Run Scenario fires an agent run (mutation/compute) —
            // disabled in adminReadOnly. View Provenance is a read-only download.
            runScenarioDisabled={isStreaming || collected < 4 || adminReadOnly}
            provenanceDisabled={!activeConversationId}
          />
          {/* admin-deal-oversight ADR 0007: VariablesPanel is display-only in
              adminReadOnly — `readOnly` disables the per-row cell-edit + the
              address-picker affordances (server 403s writes anyway). */}
          <VariablesPanel
            agentState={agentState}
            conversationId={activeConversationId}
            inputDefinitions={modelDetail?.data?.inputs}
            tableInputs={modelDetail?.data?.tableInputs}
            skippedMarketData={latestToolPayloads.skippedMarketData}
            fetchedVsApplied={latestToolPayloads.fetchedVsApplied}
            appliedMarketFields={latestToolPayloads.appliedMarketFields}
            marketApplyCounts={latestToolPayloads.marketApplyCounts}
            currentVersionId={activeConversation?.currentVersionId}
            acceptedUnderwritingVersionId={
              activeConversation?.acceptedUnderwritingVersionId
            }
            readOnly={adminReadOnly}
          />
        </div>
      )}

      {/* admin-deal-oversight ADR 0007: NewConversationModal suppressed in
          adminReadOnly — admin cannot create deals for other users. */}
      {!adminReadOnly && (
        <NewConversationModal
          open={showNewModal}
          onClose={() => setShowNewModal(false)}
          onCreate={handleCreate}
          creating={createConversation.isPending}
        />
      )}

      <ConfirmModal
        open={showForkConfirm}
        onClose={() => setShowForkConfirm(false)}
        onConfirm={handleFork}
        title="Fork Analysis"
        confirmLabel={
          forkConversation.isPending
            ? 'Forking...'
            : `Fork to v${latestVersion}`
        }
      >
        Fork &ldquo;{detail?.title}&rdquo; to v{latestVersion}? This creates a
        new analysis with your current inputs on the newer model version.
      </ConfirmModal>
    </div>
  );
}
